/* MiniMatcher.jsx, the slide-up popup used by every contextual
   recommendation placement on BuildPilot.

   Flow:
     1. (Anonymous only) Account creation, name + email + state + ToS tick
     2. Category-specific refinement questions (2-4 max)
     3. Result, matched business cards (Featured pill + Listed tier),
        per-business explicit consent, "Connect me" → records the lead

   Design constraint:
     - Visually mirrors the existing workspace aesthetic (the same fact
       pills, blue gradient, JourneyProgress pulse, soft shadows).
     - Slides up from the bottom-right on desktop / bottom on mobile.
     - 380px max width on desktop. Always dismissible.
     - "Talk to BuildPilot 1:1" link sits as a quiet footer secondary
       on every screen.

   Phase 1 (2026-05-29).
*/
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { resolveBuilderLogo } from '../../utils/builderLogoOverrides';
import { X, Check, ChevronRight, ShieldCheck, Clock, Camera, Award, MapPin, Wallet, Trophy, Briefcase, Hammer, Ruler, FileCheck, HardHat, BadgeCheck, Star, Zap, Phone, Truck, Calendar, FileText, Home, PencilRuler, Leaf, Compass, Layers, Wrench, Sun, Droplets, ArrowUpRight, BookOpen, Building2, ChefHat } from 'lucide-react';
import {
  fetchRecommendations,
  createLead,
  trackImpression,
  getOrCreateSessionId,
} from '../../services/recommendationsService';
import { getSupabase } from '../../workspace/lib/supabaseClient';
import { resolveCategoryKey, buildFindHeadline } from '../../data/taxonomy';
import MatchDisclaimer from '../trust/MatchDisclaimer';
import { AppStoreRating } from '../trust/AppStoreRating';
import MiniMatcherStateChip from './MiniMatcherStateChip';
import { getStoredUserState } from '../../lib/userState';
import { getAttribution } from '../../lib/attribution';
import { MatcherExplainerVideoModal, MatcherExplainerTrigger } from './MatcherExplainerVideo';
import BuildPilotIntelligenceBadge from './BuildPilotIntelligenceBadge';
import { ADELAIDE_SUBURBS } from '../../data/adelaideSuburbs';
import useAnonymousBrief from '../../hooks/useAnonymousBrief';
import useSessionContact from '../../hooks/useSessionContact';
import { resolveFallback } from './fallbackResolver';
import { AdvisorOffer } from '../support/AdvisorOffer';
import '../support/AdvisorOffer.css';
import GateNotReady from './GateNotReady';
import BusinessIntroVideo from '../partners/BusinessIntroVideo';

// Pre-flatten + sort so SuburbCombo renders without re-sorting every keystroke.
const _ALL_SUBURBS_SA = Object.keys(ADELAIDE_SUBURBS).sort();

/** Derive a smart "Find a Tiler" headline from a category key when
 * the caller didn't pass an explicit `heading` prop. Falls back to a
 * generic phrasing for partner/non-taxonomy categories so the popup
 * never reads as the unhelpful "Find help". */
function smartHeadingFor(categoryKey) {
  const resolved = resolveCategoryKey(categoryKey);
  if (resolved) {
    const { prefix, label } = buildFindHeadline(resolved.sub.label);
    return `${prefix} ${label}`;
  }
  // Partner: prefixes still flow through; fall back to a friendly
  // generic so we never say "Find help" again.
  if (categoryKey && categoryKey.includes(':')) {
    const after = String(categoryKey).split(':')[1] || '';
    const pretty = after.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
    if (pretty) return `Find a ${pretty}`;
  }
  return 'Find your match';
}

// Lucide icon registry for the fact pills returned by the backend
const ICONS = {
  ShieldCheck, Clock, Camera, Award, MapPin, Wallet, Trophy, Briefcase,
  Hammer, Ruler, FileCheck, HardHat, BadgeCheck, Star, Zap, Phone,
  Truck, Calendar, FileText, Home, PencilRuler, Leaf, Compass, Layers,
  Wrench, Sun, Droplets, Building2, ChefHat,
};

const AU_STATES = ['SA', 'VIC', 'NSW', 'QLD', 'WA', 'TAS', 'NT', 'ACT'];

// ─── B3: Budget reality benchmarks ─────────────────────────────────
// When a user picks a budget band that's well below typical Australian
// project pricing for the category, surface a soft nudge so the
// opportunity arrives at HQ flagged as "budget-light" (or the user
// self-corrects upward). This protects businesses from being matched
// to opps that have zero chance of converting at the user's price.
//
// `flagValues` are the option `.value` strings (from opportunities.py
// refinement_questions) that should trigger the nudge. `floor` is the
// approximate Australian floor for a viable project in that category.
const BUDGET_BENCHMARKS = {
  reno_budget: {
    flagValues: ['u50k'],
    floor: '$80k - $150k',
    msg: "Most full renovations in Australia sit between $80k and $300k once trades, finishes and waste are factored in. Builders who quote on sub-$50k whole-room renos are rare. Adjust upward if you can, otherwise we'll search hard for a specialist who'll take it.",
  },
  kitchen_budget: {
    flagValues: ['u15k'],
    floor: '$20k - $40k',
    msg: "Full kitchen renos typically land between $20k and $40k once cabinetry, benchtops and trades are in. If you're under $15k you're in cabinetry-only territory, happy to match either way.",
  },
};
// Question IDs that the renderer should treat as the "budget" question
// (used for displaying the realism nudge inline beneath the tiles).
const BUDGET_QUESTION_IDS = new Set(Object.keys(BUDGET_BENCHMARKS));

export default function MiniMatcher({
  open,
  onClose,
  category: categoryProp,
  // Optional disambiguation step: if passed (array of {label, category,
  // blurb?}), the matcher opens with a 'pick-category' step asking the
  // user which kind of supplier/trade/pro they need. Once picked, the
  // matcher runs as if that category had been passed in directly.
  categoryOptions,
  sourcePlacement,
  sourcePage,
  initialState,
  heading,
  subheading,
  anchorSide = 'right',   // 'left' | 'right', which edge the slide-up panel attaches to
}) {
  // Internal "picked" category, only used when categoryOptions is
  // supplied. Falls back to the explicit prop. All downstream code
  // continues to read `category` so we don't need to thread the new
  // state through 30+ references.
  const [pickedCategory, setPickedCategory] = useState(null);
  const category = pickedCategory || categoryProp;
  // Anonymous brief - aggregated project context for cross-component
  // continuity. We push the refinement answers into it as the user
  // progresses through the matcher so the rest of the site can read
  // their state/budget/build_type even without an account.
  const { update: updateAnonBrief } = useAnonymousBrief();
  // ─── Auth context ────────────────────────────────────────────────
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  // Explainer video lightbox - trigger sits on the modal header.
  // Declared here (near the top of the component, before any early
  // return) so `useState` obeys the Rules of Hooks.
  const [explainerOpen, setExplainerOpen] = useState(false);

  useEffect(() => {
    const supa = getSupabase();
    if (!supa) {
      setAuthReady(true);
      return;
    }
    supa.auth.getUser().then(({ data }) => {
      setUser(data?.user || null);
      setAuthReady(true);
    }).catch(() => setAuthReady(true));
    const { data: sub } = supa.auth.onAuthStateChange((_evt, session) => {
      setUser(session?.user || null);
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  // ─── Step state ───────────────────────────────────────────────────
  // Flow (questions-first; we collect personal details ONLY after
  // the user has invested 30 seconds answering refinement questions):
  //   logged-out:           'questions' → 'account' → 'verify' → 'results' → 'sent'
  //   logged-in (gap):      'questions' → 'account' → 'verify' → 'results' → 'sent'
  //   logged-in (verified): 'questions' → 'results' → 'sent'   (skip account+verify)
  //   pick-category present: 'pick-category' → 'questions' → ...
  const initialStep = (categoryOptions && !category) ? 'pick-category' : 'questions';
  const [step, setStep] = useState(initialStep);
  // Capture profile via Supabase profiles table, exposes phone + phone_verified
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!user?.id) { setProfile(null); setProfileLoaded(true); return; }
    const supa = getSupabase();
    if (!supa) { setProfileLoaded(true); return; }
    supa.from('profiles').select('*').eq('id', user.id).maybeSingle()
      .then(({ data }) => { setProfile(data || null); setProfileLoaded(true); })
      .catch(() => setProfileLoaded(true));
  }, [user]);

  // 2026-02-15: explicit concierge consent state. Was hardcoded to true
  // server-side; now bound to a real checkbox the user must tick. We
  // can't reuse `account.privacy` because the concierge short-circuit
  // bypasses the account form entirely.
  const [conciergeConsent, setConciergeConsent] = useState(false);
  const [waitlistConsent, setWaitlistConsent] = useState(false);
  // 2026-02-15: MiniMatcher-wide "gate" state. When a category's
  // refinement_questions carry `gate: true` and the user picks an
  // option with `gate_fail: true`, we set `gate` to the gate_reason
  // and switch step → 'gate'. That step renders a friendly
  // "you'll need X before we can match you" screen with educational
  // links + soft handoff to related matchers (Town Planner /
  // Building Designer). See the `professional:private_certifier`
  // entry in backend/routes/opportunities.py for the first user.
  const [gate, setGate] = useState(null);  // null | { reason: string, category: string, state: string|null }
  // 2026-06: only auto-route to the early "Shop products" step once per open.
  const shopStepShownRef = useRef(false);
  // Session-only contact memory: when a user has already filled in
  // their name / email / phone for an earlier MiniMatcher in this tab,
  // prefill those fields here so the second shortlist is one-tap.
  const { contact: sessionContact, update: updateSessionContact } = useSessionContact();
  const [account, setAccount] = useState({
    name: sessionContact.name || '',
    email: sessionContact.email || '',
    countryCode: sessionContact.countryCode || '+61',
    phone: sessionContact.phone || '',
    state: sessionContact.state || initialState || '',
    suburb: sessionContact.suburb || '',
    privacy: false,
    leadShare: false,
  });
  // OTP state, used by the inline 'verify' step
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  // 2026-02-19: Persistent state-chip at the top of every Mini Matcher
  // session - surfaces "We're matching for: NSW" across all steps so
  // users never wonder whether the filter is correct. Clicking the chip
  // opens an inline dropdown for instant override.
  const [stateChipEditing, setStateChipEditing] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0);
  const [otpDevCode, setOtpDevCode] = useState(null);     // dev-mode bypass code
  const [verifiedPhone, setVerifiedPhone] = useState(null); // E.164 number, post-OTP

  const fullPhone = useMemo(() => {
    const digits = (account.phone || '').replace(/[^0-9]/g, '').replace(/^0+/, '');
    if (!digits) return '';
    return `${account.countryCode}${digits}`;
  }, [account.countryCode, account.phone]);

  // 2026-02: Persist the user's contact details to sessionStorage as
  // soon as they have meaningful values - this is what lets a second
  // MiniMatcher (e.g. user does Suppliers, then Tradies) open with
  // every field already filled in. Wiped when the tab closes.
  useEffect(() => {
    if (!account.name && !account.email && !account.phone) return;
    updateSessionContact({
      name: account.name,
      email: account.email,
      countryCode: account.countryCode,
      phone: account.phone,
      state: account.state,
      suburb: account.suburb,
    });
  }, [account.name, account.email, account.phone, account.countryCode, account.state, account.suburb, updateSessionContact]);
  const [refinement, setRefinement] = useState({});
  // Index of the currently-displayed question inside the
  // ONE-AT-A-TIME questions step. Persisted to localStorage along
  // with the refinement answers so closing + reopening the popup
  // resumes the user exactly where they left off.
  const [currentQIdx, setCurrentQIdx] = useState(0);
  const [recs, setRecs] = useState({
    results: [],
    coverage_state: null,
    fallback: {},
    refinement_questions: [],
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [perBusinessConsent, setPerBusinessConsent] = useState({});
  // 2026-02 legal audit (Fix #6): the name of the business the user
  // just connected with, surfaced on the 'sent' confirmation step so
  // the post-handoff screen explicitly tells the user who their
  // contact details have been shared with.
  const [sentToBusinessName, setSentToBusinessName] = useState(null);

  // Partial-coverage waitlist state (the "Notify me when more join"
  // CTA on the partial banner). Lives at this scope so the UI can
  // resolve back to the success state if the user re-opens.
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState('idle');  // idle | submitting | success | error
  const [waitlistError, setWaitlistError] = useState('');

  // ─── Result freshness banner + explainer ─────────────────────
  // 2026-02-23: When a user reruns the matcher for the same
  // category their shortlist may legitimately look different -
  // builders update their availability/capacity/appetite weekly,
  // new businesses join, others go on a quiet period. Rather than
  // letting that feel "random", we surface a soft banner the
  // moment we detect a 2nd+ completed run for the same category,
  // and an always-available "Why might my results change?"
  // expandable explainer below the cards. Run count is kept in
  // localStorage namespaced by category so it survives reloads.
  const [isRerun, setIsRerun] = useState(false);
  const [whyChangeOpen, setWhyChangeOpen] = useState(false);
  const _runsKey = useCallback((cat) => `bp.mm.runs.${cat || 'unknown'}`, []);
  useEffect(() => {
    if (step !== 'results') return;
    const hasResults = (recs.results || []).length > 0;
    if (!hasResults) return;
    try {
      const key = _runsKey(category);
      const prior = parseInt(localStorage.getItem(key) || '0', 10) || 0;
      if (prior > 0) setIsRerun(true);
      localStorage.setItem(key, String(prior + 1));
    } catch { /* localStorage unavailable - silently ignore */ }
  }, [step, recs.results, category, _runsKey]);

  // ─── Inline sign-in lane ─────────────────────────────────────────
  // 2026-06-05: if the user already has a BuildPilot account, let
  // them sign in from inside the matcher rather than bouncing them
  // to the global Auth Modal. After a successful sign-in we drop
  // them into the OTP step (or skip it if their phone is already
  // verified) so they reach matches in one continuous flow.
  const [emailExistsHint, setEmailExistsHint] = useState(false);
  const [signinOpen, setSigninOpen] = useState(false);
  const [signinPassword, setSigninPassword] = useState('');
  const [signinSubmitting, setSigninSubmitting] = useState(false);
  const [signinError, setSigninError] = useState('');

  // ─── Persistence: resume on close + reopen ────────────────────
  // The matcher persists in-flight question progress (which question
  // the user is on + their answers so far) per category to
  // localStorage, so closing the popup mid-flow and reopening it
  // (even after a hard refresh) drops the user back where they left
  // off rather than reset to question 1. Key is namespaced by
  // category so different matchers don't trample each other.
  const _progressKey = useCallback((cat) => `bp.mm.progress.${cat || 'unknown'}`, []);
  const _saveProgress = useCallback((cat, payload) => {
    if (typeof window === 'undefined' || !cat) return;
    try { window.localStorage.setItem(_progressKey(cat), JSON.stringify(payload)); } catch { /* ignore */ }
    // 2026-02-15 Slice A: also fire a matcher_answer event so HQ has
    // an event-stream view of in-progress briefs (not just submitted
    // ones). Throttled implicitly by being called from onChange.
    try {
      // eslint-disable-next-line global-require
      const { trackEvent } = require('../../lib/trackEvent');
      trackEvent('matcher_answer', {
        category: cat,
        q_idx: payload?.qIdx,
        answers: payload?.refinement,
      }, { user });
    } catch { /* swallow */ }
  }, [_progressKey, user]);
  const _loadProgress = useCallback((cat) => {
    if (typeof window === 'undefined' || !cat) return null;
    try {
      const raw = window.localStorage.getItem(_progressKey(cat));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch { return null; }
  }, [_progressKey]);
  const _clearProgress = useCallback((cat) => {
    if (typeof window === 'undefined' || !cat) return;
    try { window.localStorage.removeItem(_progressKey(cat)); } catch { /* ignore */ }
  }, [_progressKey]);

  // Reset whenever the popup is opened with a different category
  useEffect(() => {
    if (!open) return;
    if (!profileLoaded) return;   // wait until we know the user's verified state
    setError('');
    // Restore persisted progress (refinement answers + current question
    // index) for the active category, if any. We intentionally restore
    // BEFORE the empty reset paths below so the user keeps their
    // answers across close+reopen sessions.
    const resumeCat = pickedCategory || categoryProp;
    const saved = _loadProgress(resumeCat);
    const savedRef = saved?.refinement || {};
    setRefinement(savedRef);
    setCurrentQIdx(typeof saved?.qIdx === 'number' ? saved.qIdx : 0);
    // 2026-02-15: initial recs has coverage_state=null so the zero-
    // coverage short-circuit useEffect doesn't fire on the very first
    // render before the real fetch lands. The check
    //   `if (cov !== 'concierge' && cov !== 'disabled') return;`
    // returns early for null, which is exactly what we want until the
    // backend tells us a real coverage state.
    setRecs({ results: [], coverage_state: null, fallback: {}, refinement_questions: [] });
    setPerBusinessConsent({});
    setSentToBusinessName(null);
    // Reset gate state per open so a previous gate result doesn't
    // leak into a new category's questions flow.
    setGate(null);
    // Reset waitlist UI per open so a previously-submitted category
    // doesn't bleed into a new session.
    setWaitlistEmail('');
    setWaitlistStatus('idle');
    setWaitlistError('');
    // Reset sign-in lane state per open.
    setEmailExistsHint(false);
    setSigninOpen(false);
    setSigninPassword('');
    setSigninError('');
    setOtpCode('');
    setOtpDevCode(null);
    setOtpResendIn(0);
    // Drop any previously-picked category when reopening with options
    // (the parent may swap which menu we came from between opens).
    if (categoryOptions) setPickedCategory(null);

    // Pre-fill account fields from profile + user_metadata for logged-in users.
    // For anonymous users we fall back to the session-only contact memory
    // (sessionStorage) so a 2nd / 3rd MiniMatcher run inside the same tab
    // doesn't make the user re-type name / email / phone.
    const profileName = profile?.full_name || user?.user_metadata?.full_name || sessionContact.name || '';
    const profileEmail = user?.email || profile?.email || sessionContact.email || '';
    const profilePhone = profile?.phone || user?.user_metadata?.phone || '';
    // 2026-02-19: State pre-fill rules. Priority order:
    //   1. Logged-in user's profile.state (authoritative)
    //   2. Parent-supplied `initialState` (always intentional -
    //      passed only by mega-menu, pillar CTAs, /matcher deep-links,
    //      recommendation strips, and Journey handoffs, never leaked
    //      from a wrong-state source).
    //   3. Any state the user has already typed into a lead capture
    //      earlier in the session.
    //   4. Geo-detected state (soft IP default from lib/userState) - fills
    //      the gap for anonymous visitors so the "which state" question
    //      auto-skips instead of losing ~half of them at Q0. Uses the
    //      null-returning stored lookup (NOT the SA-default getUserState)
    //      so a truly-unknown visitor still gets asked.
    //   5. Empty - forces the "Which state are you building in?"
    //      question to show.
    // 2026-02-28: relaxed - `initialState` no longer gated on `user`.
    // The previous gate blocked pillar-page CTAs (e.g. Adelaide pool
    // builder) from skipping the state question for anonymous users
    // even though the pillar is SA-only by construction.
    const knownState = getStoredUserState(); // null when we don't actually know
    const profileState = profile?.state || initialState || sessionContact.state || knownState || '';
    const profileSuburb = profile?.suburb || user?.user_metadata?.suburb || sessionContact.suburb || '';

    let phoneCC = sessionContact.countryCode || '+61';
    let phoneLocal = profilePhone || sessionContact.phone || '';
    if (profilePhone && profilePhone.startsWith('+')) {
      // Naive split, E.164 numbers start with +<country><number>. We
      // try the most common AU prefix first; if the stored number is
      // not AU we still surface it correctly in the country dropdown
      // because the user will be able to change it.
      const m = profilePhone.match(/^(\+\d{1,3})(.*)$/);
      if (m) { phoneCC = m[1]; phoneLocal = m[2]; }
    }
    setAccount({
      name: profileName,
      email: profileEmail,
      countryCode: phoneCC,
      phone: phoneLocal,
      state: profileState,
      suburb: profileSuburb,
      privacy: false,
      leadShare: false,
    });
    // 2026-02-19: Seed the refinement.state from the user's known state
    // so the server-side "Which state are you building in?" question
    // (always Q1) auto-skips for known-state users. Unknown-state users
    // see the pill-style question as the first thing in their brief.
    setRefinement((r) => (profileState && !r.state ? { ...r, state: profileState } : r));
    if (profile?.phone_verified) {
      setVerifiedPhone(profile.phone);
    } else {
      setVerifiedPhone(null);
    }

    // Decide where to start. If categoryOptions were supplied but
    // the user hasn't picked one yet, start at the picker; otherwise
    // questions-first for everyone (logged-in verified users with full
    // profile still skip the account step after questions).
    setStep((categoryOptions && !categoryProp) ? 'pick-category' : 'questions');
    // NOTE on deps: we intentionally depend on `categoryProp` (the raw
    // prop), NOT the derived `category` alias. If we depended on the
    // alias, every picker click would change `pickedCategory` → change
    // `category` → re-run this effect → reset pickedCategory and step
    // back to 'pick-category', which is exactly what made the picker
    // buttons appear "not work" in the bug report.
  }, [open, categoryProp, categoryOptions, user, profile, profileLoaded, initialState]);

  // OTP resend countdown
  useEffect(() => {
    if (otpResendIn <= 0) return undefined;
    const t = setTimeout(() => setOtpResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);

  // Persist question progress (answers + current question index) to
  // localStorage on every change inside the 'questions' step. The
  // matching restore happens in the open-reset effect above.
  useEffect(() => {
    if (!open) return;
    if (step !== 'questions') return;
    const cat = pickedCategory || categoryProp;
    if (!cat) return;
    _saveProgress(cat, { refinement, qIdx: currentQIdx });
  }, [open, step, pickedCategory, categoryProp, refinement, currentQIdx, _saveProgress]);

  // ─── Jump past already-answered questions on mount/refinement-load ─
  // When the refinement_questions list arrives from the backend, if
  // the user already has answers for the leading questions (e.g.
  // build_type prefilled from a pick-category pill, or restored
  // from localStorage), advance currentQIdx to the first unanswered
  // one so we don't re-ask them.
  useEffect(() => {
    if (!open || step !== 'questions') return;
    const qs = recs.refinement_questions || [];
    if (qs.length === 0) return;
    // If the persisted/current index already points at an unanswered
    // question, leave it alone.
    const isAnsweredAt = (idx) => {
      const q = qs[idx];
      if (!q) return false;
      // Hidden (show_if not met) counts as "already handled" so we
      // walk past it.
      if (!_shouldShowQ(q, refinement)) return true;
      // 2026-06: Auto-skip the "which state" question when we already
      // know the state (profile, an intentional initialState, or the
      // geo/soft IP default). This kills the Q0 wall that was losing
      // ~48% of builder sessions at the very first step. The persistent
      // "matching for: <STATE>" chip above stays visible and one-tap
      // changeable, so there's no risk of a silent national shortlist -
      // which is the only reason this was previously force-shown.
      if (q.id === 'state') return !!refinement.state;
      const v = refinement[q.id];
      if (q.type === 'multiselect') return Array.isArray(v) && v.length > 0;
      return v !== undefined && v !== null && v !== '';
    };
    if (!isAnsweredAt(currentQIdx)) return;
    // Walk forward to the first unanswered index.
    let n = currentQIdx;
    while (n < qs.length && isAnsweredAt(n)) n += 1;
    if (n !== currentQIdx) setCurrentQIdx(n);
    // We intentionally only depend on the refinement_questions array
    // identity (loads once per open) - not on `refinement`, otherwise
    // every answer click would re-trigger this skip logic and
    // override the auto-advance in finishOrNext.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, recs.refinement_questions]);

  // 2026-06: Race-proof Q0 skip. If the state answer is seeded AFTER the
  // questions arrive (geo/profile timing), advance past the leading
  // state question so known-state users never land on the Q0 wall.
  useEffect(() => {
    if (!open || step !== 'questions') return;
    const qs = recs.refinement_questions || [];
    if (currentQIdx !== 0) return;
    if (qs[0]?.id === 'state' && refinement.state) setCurrentQIdx(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, recs.refinement_questions, refinement.state]);

  // Load refinement questions + results on mount (we need the question
  // list even before the user has answered, coverage state may flip
  // us straight to a fallback).
  useEffect(() => {
    if (!open || !category) return;
    let cancelled = false;
    setLoading(true);
    const effectiveState = account.state || initialState || null;
    fetchRecommendations({
      category,
      state: effectiveState,
      sourcePlacement,
      userId: user?.id,
      sessionId: getOrCreateSessionId(),
    }).then((data) => {
      if (cancelled) return;
      setRecs(data);
      setLoading(false);
      // 2026-02-19: Defer impression tracking until we have a confirmed
      // state. Without it, businesses from default/null state pollute
      // the impression analytics for users who never see them.
      if (!effectiveState) return;
      (data.results || []).forEach((b, i) => trackImpression({
        category,
        sourcePlacement,
        businessId: b.id,
        partnerKey: b.partner_key,
        userId: user?.id,
        sessionId: getOrCreateSessionId(),
        state: effectiveState,
        shownPosition: i + 1,
        clicked: false,
      }));
    }).catch(() => {
      if (cancelled) return;
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, category, account.state, initialState, sourcePlacement, user]);

  // 2026-02-17 / 2026-02-15 (reversed) - Zero-coverage short-circuit.
  // PREVIOUS RULE: when the backend reported `coverage_state` of
  // 'concierge' or 'disabled', we'd skip straight to the results
  // screen so users didn't dead-end at a 12-Q gauntlet → concierge
  // message.
  // NEW RULE (per product owner 2026-02-15): always walk the user
  // through the question brief FIRST - matches mega-menu behaviour.
  // The concierge fallback still shows at the end (rendered inside
  // the results step) but now we have a qualified lead context to
  // hand back to the user (and to HQ via marketing_captures).
  // We keep the short-circuit ONLY for the edge case where the
  // backend has no questions to ask (refinement_questions empty),
  // because there's nothing to show on the questions step.
  useEffect(() => {
    if (!open) return;
    if (step !== 'questions') return;
    // Wait for the real fetch to land before deciding to short-circuit;
    // the initial `recs` state is `disabled` + empty questions, which
    // would otherwise fire this skip immediately on open.
    if (loading) return;
    const cov = recs.coverage_state;
    if (cov !== 'concierge' && cov !== 'disabled') return;
    const qs = recs.refinement_questions || [];
    if (qs.length > 0) return; // run the brief normally
    // No questions to ask AND no coverage → there's nothing to do
    // on the questions step; jump straight to the concierge view.
    if (refinement && Object.keys(refinement).length > 0) return;
    setStep('results');
  }, [open, step, loading, recs.coverage_state, recs.refinement_questions, refinement]);

  // 2026-06: Early "Shop products" surfacing. As soon as the category +
  // location are known and the backend returns affiliate (shop) suppliers,
  // route to the dedicated 'shop' step BEFORE the brief - affiliate
  // suppliers need no questions and no OTP, just a tracked click to store.
  // For mixed categories the shop step offers a "continue to get quotes"
  // button into the normal brief; for shop-only categories it's terminal.
  useEffect(() => {
    if (!open || loading) return;
    if (step !== 'questions') return;         // only intercept at the start
    if (shopStepShownRef.current) return;
    const effectiveState = account.state || initialState;
    if (!effectiveState) return;              // wait until location is known
    const shopR = (recs.results || []).filter((b) => b.result_kind === 'shop' || b.result_kind === 'quote');
    if (shopR.length === 0) return;           // no affiliate/quote suppliers -> normal brief
    shopStepShownRef.current = true;
    setStep('shop');
  }, [open, loading, step, recs.results, account.state, initialState]);

  // Reset the one-shot shop-step flag whenever the matcher is reopened.
  useEffect(() => {
    if (!open) shopStepShownRef.current = false;
  }, [open]);

  const advanceFromAccount = useCallback(async () => {
    setError('');
    if (!account.name.trim()) return setError('Please enter your full name.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(account.email)) return setError('Please enter a valid email.');
    const digits = (account.phone || '').replace(/[^0-9]/g, '').replace(/^0+/, '');
    if (digits.length < 8 || digits.length > 12) return setError('Please enter a valid mobile number.');
    if (!account.state) return setError('Please select your state.');
    if (!account.suburb.trim() || account.suburb.trim().length < 2) return setError('Please enter the suburb where the work is.');
    if (!account.privacy) return setError('Please accept the Privacy Policy and Terms to continue.');
    if (!account.leadShare) return setError('Please confirm BuildPilot may introduce you to matched businesses.');

    // Skip OTP if logged-in user's phone already matches a previously verified number
    if (user && profile?.phone_verified && profile?.phone === fullPhone) {
      setVerifiedPhone(fullPhone);
      setStep('results');
      return;
    }

    // Send OTP, uses the existing /api/builder-matchmaker/send-otp
    // endpoint (Twilio under the hood, with dev-mode bypass when
    // Twilio env vars are not configured). 15s abort timeout so an
    // iOS Safari network hiccup doesn't leave the button stuck on
    // "Sending code…" indefinitely.
    setOtpSending(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      let res;
      try {
        res = await fetch(`${apiUrl}/api/builder-matchmaker/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: fullPhone }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not send verification code');
      setOtpDevCode(data?.dev_mode ? data.code : null);
      setOtpResendIn(45);
      // Persist the contact + partial brief the moment the code goes out,
      // so an "entered details but didn't verify" lead is never lost.
      // Fire-and-forget to Supabase marketing_captures; never blocks the UX.
      try {
        fetch(`${apiUrl}/api/matcher/otp-pending-capture`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            email: account.email,
            name: account.name || null,
            phone: fullPhone || null,
            category: category || null,
            state: account.state || initialState || null,
            suburb: account.suburb || null,
            partial_brief: refinement,
            source_page: sourcePage || (typeof window !== 'undefined' ? window.location.pathname : null),
            source_placement: sourcePlacement || null,
            session_id: getOrCreateSessionId(),
            privacy_consent: true,
            lead_sharing_consent: true,
            utm: getAttribution(),
          }),
        }).catch(() => {});
      } catch { /* ignore */ }
      setStep('verify');
    } catch (e) {
      const msg = e?.name === 'AbortError'
        ? 'The request took too long. Check your connection and tap Continue again.'
        : (e.message || 'Could not send verification code. Please try again.');
      setError(msg);
    } finally {
      setOtpSending(false);
    }
  }, [account, fullPhone, user, profile]);

  // Verify the OTP entered by the user. On success: persist the
  // verified phone to their profile (if logged in) and advance to
  // the questions step.
  const verifyPhoneOtp = useCallback(async () => {
    setError('');
    const code = (otpCode || '').replace(/[^0-9]/g, '');
    if (code.length !== 6) return setError('Enter the 6-digit code we sent you.');
    setOtpVerifying(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/builder-matchmaker/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: fullPhone, code }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) throw new Error(data?.detail || "That code didn't match. Please try again.");

      setVerifiedPhone(fullPhone);

      // If logged in, persist phone + phone_verified + the captured
      // location context to the user's profile so subsequent matchers
      // don't have to re-ask. We also stamp the most recent
      // category-of-interest so the workspace can personalise the
      // dashboard without an extra query.
      if (user?.id) {
        try {
          const supa = getSupabase();
          if (supa) {
            await supa.from('profiles').update({
              phone: fullPhone,
              phone_verified: true,
              full_name: account.name || profile?.full_name,
              state: account.state || profile?.state,
              suburb: account.suburb || profile?.suburb,
              last_category_interest: category || profile?.last_category_interest,
              updated_at: new Date().toISOString(),
            }).eq('id', user.id);
          }
        } catch { /* best-effort, lead still proceeds */ }
      } else {
        // 2026-06-05: Removed the post-verify magic-link sign-in
        // (`supa.auth.signInWithOtp({ email })`). Mobile OTP is the
        // verification path, sending an email magic link on top of
        // that confused users into thinking they had to click two
        // things to "complete" their account. The lead is still
        // recorded server-side, BuildPilot still emails the user
        // their confirmation, and HQ is still notified. When the
        // user later wants to access their workspace, the regular
        // Auth Modal lets them sign in (email OTP or password).
      }

      setStep('results');
    } catch (e) {
      setError(e.message || "That code didn't match. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  }, [otpCode, fullPhone, user, account, profile, sourcePlacement, category, refinement]);

  const resendOtp = useCallback(async () => {
    if (otpResendIn > 0) return;
    setError('');
    setOtpSending(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      let res;
      try {
        res = await fetch(`${apiUrl}/api/builder-matchmaker/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: fullPhone }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not resend code');
      setOtpDevCode(data?.dev_mode ? data.code : null);
      setOtpResendIn(45);
    } catch (e) {
      const msg = e?.name === 'AbortError'
        ? 'The request took too long. Check your connection and try again.'
        : (e.message || 'Could not resend code. Please try again.');
      setError(msg);
    } finally {
      setOtpSending(false);
    }
  }, [fullPhone, otpResendIn]);

  const refinementQs = recs.refinement_questions || [];

  // ─── show_if support ─────────────────────────────────────────────
  // Backend question schemas can carry a `show_if` map of
  // { otherQuestionId: [allowedValue, ...] }. The renderer treats
  // questions whose conditions aren't met as "skipped" - they don't
  // count toward the visible total, don't block completion, and the
  // walker skips them on next/prev.
  const _shouldShowQ = useCallback((q, ref) => {
    if (!q || !q.show_if) return true;
    return Object.entries(q.show_if).every(([k, allowed]) => {
      const v = ref?.[k];
      if (!Array.isArray(allowed)) return v === allowed;
      return allowed.includes(v);
    });
  }, []);

  // "Notify me when more join" submit handler. Hits the new
  // /coverage-waitlist endpoint; idempotent on (email, category, state)
  // so repeated submissions don't spam the collection.
  const submitWaitlist = useCallback(async () => {
    if (waitlistStatus === 'submitting') return;
    const email = (waitlistEmail || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setWaitlistError('Enter a valid email so we can notify you.');
      return;
    }
    // 2026-02-15: explicit consent required (was previously bypassed).
    const hasConsent = !!user || waitlistConsent;
    if (!hasConsent) {
      setWaitlistError('Please accept the Privacy Policy + Terms to be notified.');
      return;
    }
    setWaitlistError('');
    setWaitlistStatus('submitting');
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const body = {
        email,
        category,
        state: account.state || null,
        category_label: recs?.fallback?.category_label || null,
        source_page: typeof window !== 'undefined' ? window.location.pathname : null,
        source_placement: sourcePlacement,
        privacy_consent: true,
        session_id: getOrCreateSessionId(),
      };
      const res = await fetch(`${apiUrl}/api/coverage-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || 'Could not save. Try again.');
      }
      setWaitlistStatus('success');
    } catch (e) {
      setWaitlistStatus('idle');
      setWaitlistError(e.message || 'Could not save. Try again.');
    }
  }, [waitlistEmail, waitlistStatus, category, account.state, recs, sourcePlacement, user, waitlistConsent]);

  const refinementComplete = useMemo(
    () => refinementQs.every((q) => {
      // Hidden questions don't gate completion.
      if (!_shouldShowQ(q, refinement)) return true;
      const v = refinement[q.id];
      if (q.type === 'multiselect') return Array.isArray(v) && v.length > 0;
      return v !== undefined && v !== null && v !== '';
    }),
    [refinementQs, refinement, _shouldShowQ],
  );

  const advanceToResults = useCallback((opts = {}) => {
    // `skipCompleteness` is true when this call is fired by the
    // auto-advance after the LAST question's pill click. At that
    // point `refinementComplete` (a useMemo) may still reflect the
    // pre-click closure because React hasn't re-rendered yet, so
    // it would wrongly report "incomplete" and flash the warning.
    // The caller has already proven there are no unanswered visible
    // questions via `nextUnanswered`, so we trust them and skip the
    // redundant check.
    if (!opts.skipCompleteness && !refinementComplete) {
      setError('Please answer the questions above so we can match you.');
      return;
    }
    setError('');
    // Push the refinement answers into the anonymous brief so the
    // rest of the site (homepage, CoPilot, etc.) can read this user's
    // project context even before they sign in. PII stripping happens
    // inside the hook.
    try {
      const top = {
        state: refinement.state,
        region: refinement.region,
        suburb: refinement.suburb || refinement.land_suburb,
        build_type: refinement.build_type,
        build_goal: refinement.build_goal,
        budget: refinement.budget,
        bedrooms: refinement.bedrooms,
        timing: refinement.construction_start || refinement.urgency || refinement.timing,
        priorities: Array.isArray(refinement.priorities) ? refinement.priorities : undefined,
        block_width: refinement.block_width,
      };
      updateAnonBrief(top, { category, answers: refinement });
    } catch { /* best-effort */ }
    // ─── Builder handoff ─────────────────────────────────────────
    // The 'professional:builder' flow has an 11-question schema that
    // feeds the full-screen Find-a-Builder workspace tool (OTP gate
    // → matched builders → floor plan quiz → quotes). After the
    // user finishes Q11 inside the popup we stash the answers in
    // sessionStorage and hand off to that tool, which auto-submits
    // and jumps straight to the lead-gate / results view (skipping
    // its own quiz UI).
    // 2026-02-20: ALWAYS hand off for professional:builder regardless
    // of what /opportunities/recommend returned. That endpoint queries
    // `business_profiles` which has no builder rows, so it permanently
    // returns concierge for this category - but the wizard's own
    // /api/builder-matchmaker/submit endpoint queries the `builders`
    // table (27+ active rows). The wizard is the source of truth for
    // builders, so the popup must always route there. Previous logic
    // (suppress handoff when recommend returned concierge) effectively
    // killed every builder match because of this table mismatch.
    if (category === 'professional:builder' && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(
          'bp.findbuilder.mm.handoff',
          JSON.stringify({ answers: refinement, ts: Date.now() }),
        );
      } catch { /* ignore */ }
      _clearProgress(category);
      onClose?.();
      window.location.assign('/workspace/tools/find-a-builder?skip-matcher=1&mm-handoff=1');
      return;
    }
    // Questions are done. If the user is fully verified we can show
    // matches immediately. Otherwise we collect their details now so
    // the business they pick gets a verified-mobile lead.
    const fullyVerified = !!user
      && !!profile?.phone_verified
      && !!profile?.phone
      && !!(account.name || profile?.full_name)
      && !!(account.email || user.email);
    if (fullyVerified) {
      setVerifiedPhone(profile.phone);
      setStep('results');
    } else {
      setStep('account');
    }
    // Clear persisted progress now that the user has moved past
    // the questions step.
    _clearProgress(category);
  }, [refinementComplete, user, profile, account, category, refinement, _clearProgress, onClose, updateAnonBrief]);

  // ─── Email-exists detection (debounced) ─────────────────────────
  // While the user types their email in the account step we softly
  // check whether a BuildPilot account already exists for it. If yes
  // we reveal a "Sign in instead" link so they don't have to leave
  // the popup. Returns `{ exists: bool }` only - no PII leak.
  useEffect(() => {
    if (user) return;                       // already signed in
    if (step !== 'account') return;
    const email = (account.email || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailExistsHint(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(
          `${apiUrl}/api/auth/email-exists?email=${encodeURIComponent(email)}`,
          { method: 'GET' },
        );
        if (!res.ok) { setEmailExistsHint(false); return; }
        const data = await res.json();
        setEmailExistsHint(!!data?.exists);
      } catch {
        setEmailExistsHint(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [account.email, step, user]);

  // ─── Inline sign-in submit ──────────────────────────────────────
  // Triggered by the "Sign in instead" panel inside the account step.
  // On success we let the parent `useAuth` reload the profile, then
  // either jump straight to results (if their phone is already
  // verified + matches) or to the OTP step otherwise.
  const submitSignin = useCallback(async () => {
    const email = (account.email || '').trim();
    const password = signinPassword;
    if (!email || !password) {
      setSigninError('Enter your email and password to sign in.');
      return;
    }
    setSigninError('');
    setSigninSubmitting(true);
    try {
      const supa = getSupabase();
      if (!supa) throw new Error('Sign-in is temporarily unavailable. Try again in a moment.');
      const { data, error: e } = await supa.auth.signInWithPassword({ email, password });
      if (e || !data?.user) {
        throw new Error(e?.message || 'Could not sign in. Check your email and password.');
      }
      // Auth state will re-hydrate via the AuthContext effect. The
      // matcher's per-open useEffect will then re-run and pre-fill the
      // account fields from the freshly-loaded profile. From here we
      // just close the sign-in lane; the rest of the flow continues
      // as if they'd been logged in all along.
      setSigninOpen(false);
      setSigninPassword('');
    } catch (err) {
      setSigninError(err.message || 'Could not sign in. Try again.');
    } finally {
      setSigninSubmitting(false);
    }
  }, [account.email, signinPassword]);


  const connectMe = useCallback(async (business) => {
    setSubmitting(true);
    setError('');
    const consent = perBusinessConsent[business.partner_key || business.id];
    if (!consent) {
      setError('Please tick the consent box to confirm the introduction.');
      setSubmitting(false);
      return;
    }
    const payload = {
      category,
      subcategory: business.subcategory || null,
      businessId: business.id || null,
      businessName: business.business_name || null,
      partnerKey: business.partner_key || null,
      sourcePlacement,
      sourcePage,
      state: account.state || initialState || null,
      suburb: account.suburb || null,
      refinementAnswers: refinement,
      contactName: account.name || profile?.full_name || user?.user_metadata?.full_name,
      contactEmail: user?.email || account.email,
      contactPhone: verifiedPhone || profile?.phone || null,
      userId: user?.id || null,
      privacyConsent: true,
      leadSharingConsent: true,
    };
    const res = await createLead(payload);
    setSubmitting(false);
    if (res.status === 'error') {
      setError(res.message || 'Could not complete the introduction. Please try again.');
      return;
    }
    setSentToBusinessName(business.business_name || null);
    setStep('sent');
    // Signal to matcher prompt-state hook that this category is now
    // "sorted" so we stop pushing shortlist CTAs to this user.
    try {
      window.dispatchEvent(new CustomEvent('buildpilot:matcher-submitted', { detail: { category } }));
    } catch { /* ignore */ }
  }, [perBusinessConsent, category, sourcePlacement, sourcePage, account, initialState, refinement, user, profile, verifiedPhone]);

  // Concierge submit, fires when no real businesses are matched yet
  // and BuildPilot HQ will manually make the introduction. Reuses
  // the same lead-create endpoint; the lack of business_id/partner_key
  // tags the lead as 'concierge' server-side.
  const submitConcierge = useCallback(async () => {
    setSubmitting(true);
    setError('');
    // Anonymous concierge users may have entered just their email
    // (no name/phone/OTP because the matcher short-circuited the
    // long flow when coverage_state told us nobody is onboarded).
    // Validate the email here so the lead is actually useful.
    const concEmail = (user?.email || account.email || '').trim();
    if (!concEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(concEmail)) {
      setSubmitting(false);
      setError('Please enter your email so we can let you know.');
      return;
    }
    // 2026-02-15: real consent required - was previously hardcoded to true
    // server-side without ever showing a checkbox. Logged-in users who
    // already accepted ToS on signup auto-pass.
    const hasConsent = !!user || conciergeConsent;
    if (!hasConsent) {
      setSubmitting(false);
      setError('Please accept the Privacy Policy + Terms to keep posted.');
      return;
    }
    const payload = {
      category,
      subcategory: null,
      businessId: null,
      partnerKey: null,
      sourcePlacement,
      sourcePage,
      state: account.state || initialState || null,
      suburb: account.suburb || null,
      refinementAnswers: refinement,
      contactName: account.name || profile?.full_name || user?.user_metadata?.full_name,
      contactEmail: user?.email || account.email,
      contactPhone: verifiedPhone || profile?.phone || null,
      userId: user?.id || null,
      privacyConsent: true,
      leadSharingConsent: true,
    };
    const res = await createLead(payload);
    setSubmitting(false);
    if (res.status === 'error') {
      setError(res.message || 'Could not submit your request. Please try again.');
      return;
    }
    setStep('sent');
    // Signal to matcher prompt-state hook that this category is now
    // "sorted" (via concierge) so we stop pushing shortlist CTAs.
    try {
      window.dispatchEvent(new CustomEvent('buildpilot:matcher-submitted', { detail: { category } }));
    } catch { /* ignore */ }
  }, [category, sourcePlacement, sourcePage, account, initialState, refinement, user, profile, verifiedPhone, conciergeConsent]);

  if (!open) return null;

  const isPartnerOnly = (recs.results || []).every((b) => b.is_partner);
  const noCoverage = recs.coverage_state === 'disabled';

  // 2026-06: affiliate suppliers surface as a light "Shop these products"
  // strip BEFORE the brief - no questions, no OTP, just a tracked click to
  // their store. Lead-capture (distribution) suppliers keep the full brief
  // + verify flow and show at the MATCHES step.
  const shopResults = (recs.results || []).filter((b) => b.result_kind === 'shop' || b.result_kind === 'quote');
  const leadResults = (recs.results || []).filter((b) => b.result_kind !== 'shop' && b.result_kind !== 'quote');
  const shopOnly = shopResults.length > 0 && leadResults.length === 0;
  const shopHasRetail = shopResults.some((b) => b.result_kind === 'shop');

  // Dynamic header title: when the user is inside the QUESTIONS step
  // the title becomes the CURRENT question text (e.g. "Which state
  // are you building in?") so the popup reads like a single
  // interview question, not a generic "What are you building?"
  // wrapper. Falls back to the `heading` prop / smart default
  // everywhere else.
  const activeQuestion = (step === 'questions' && (recs.refinement_questions || [])[Math.max(0, Math.min(currentQIdx, (recs.refinement_questions || []).length - 1))]) || null;
  const headerTitle = activeQuestion?.q || heading || smartHeadingFor(category);
  // Hide the descriptive subheading entirely - every question is
  // self-explanatory and the user explicitly asked to remove it.
  const showSubheading = false;

  const overlay = (
    <div className={`bp-mm-overlay bp-mm-anchor-${anchorSide}`} role="dialog" aria-modal="true" data-testid="mini-matcher">
      <button type="button" className="bp-mm-scrim" aria-label="Close" onClick={onClose} />

      <aside className="bp-mm-panel">
        <header className="bp-mm-head bp-mm-intel-header">
          <div>
            <h3 className="bp-mm-title" data-testid="mm-header-title">{headerTitle}</h3>
            {showSubheading && (
              <p className="bp-mm-sub">{subheading}</p>
            )}
            <span className="bp-mm-intel-tagline" aria-hidden="true" style={{ marginTop: 8 }}>
              <span className="bp-mm-intel-tagline-dot" />
              Powered by BuildPilot Intelligence · Suggestions only · not endorsements
            </span>
            {/* 2026-02-28: explainer video trigger on the modal header.
                Highest-intent moment - user has just opened the matcher
                but not started answering yet. Opens a lightbox with a
                60s "how it works" video. Additive - does not block or
                delay the wizard. */}
            <div style={{ marginTop: 10 }}>
              <MatcherExplainerTrigger
                onClick={() => setExplainerOpen(true)}
                variant="ghostDark"
                testId="mini-matcher-explainer-trigger"
                label="Watch how it works"
              />
            </div>
          </div>
          <button type="button" className="bp-mm-close" onClick={onClose} aria-label="Close" data-testid="mini-matcher-close">
            <X size={18} />
          </button>
        </header>

        {/* progress dots, Questions, Details, Verify, Matches.
            We collapse Details + Verify out of the indicator when the
            logged-in user is already verified (those steps are skipped).
            The pick-category step (if any) is rolled into the
            Questions stage for the progress UI. */}
        {(!authReady || step === 'shop') ? null : (() => {
          const fullyVerified = !!user && !!profile?.phone_verified && profile?.phone === fullPhone && account.name && account.email;
          const labels = fullyVerified
            ? ['Questions', 'Matches']
            : ['Questions', 'Details', 'Verify', 'Matches'];
          const orderMap = fullyVerified
            ? { 'pick-category': 0, questions: 0, results: 1, sent: 1 }
            : { 'pick-category': 0, questions: 0, account: 1, verify: 2, results: 3, sent: 3 };
          return (
            <ol className="bp-mm-steps" aria-hidden="true">
              {labels.map((label, i) => {
                const active = orderMap[step] === i;
                const done = orderMap[step] > i;
                return (
                  <li
                    key={label}
                    className={`bp-mm-step ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`}
                  >
                    <span className="bp-mm-step-dot">{done ? <Check size={10} strokeWidth={3} /> : null}</span>
                    <span className="bp-mm-step-label">{label}</span>
                  </li>
                );
              })}
            </ol>
          );
        })()}

        <div className="bp-mm-body">
          {/* 2026-02-19: Persistent "matching for: <STATE>" chip - shared
              component used by all three mini matchers (this one,
              MiniHnlMatcher, MiniHomeDesignsMatcher) for identical UX. */}
          <MiniMatcherStateChip
            state={account.state || initialState || ''}
            region={refinement.region || ''}
            onChange={(v) => {
              setAccount({ ...account, state: v });
              setRefinement({ ...refinement, state: v, region: '' });
            }}
          />

          {/* ─── Pick-category step ─────────────────────────────
              Only shown when the parent provided a `categoryOptions`
              array. Lets the user disambiguate broad shortlists like
              "Suppliers" or "Trades" into a specific category before
              the matcher starts asking refinement questions. */}
          {step === 'pick-category' && categoryOptions && (
            <div className="bp-mm-pick-category">
              {/* 2026-06-05: Pill-with-icon layout (matches homepage
                  JourneyChips). When options carry `iconSrc` we render
                  them as rounded pills with the AVIF icon; otherwise
                  fall back to the original 2-column card grid.
                  2026-02-18: Categories with a `blurb` (the matcher
                  drill-down - Trades / Suppliers / Pros) always use
                  the richer card grid below so users see what each
                  category covers; only the JourneyChip-style pills
                  (no blurb, iconSrc only) keep the compact pill row. */}
              {categoryOptions[0]?.iconSrc && !categoryOptions[0]?.blurb ? (
                <div className="bp-mm-pick-pills" data-testid="mm-pick-pills">
                  {categoryOptions.map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        // 2026-06-05: Pills with a `redirectTo` URL
                        // short-circuit the matcher's own refinement
                        // flow and hand the user off to a deeper
                        // workspace tool (e.g. Find a Builder).
                        if (opt.redirectTo) {
                          onClose?.();
                          if (typeof window !== 'undefined') {
                            window.location.assign(opt.redirectTo);
                          }
                          return;
                        }
                        // 2026-feb: `prefill` lets the pill seed one
                        // or more refinement answers (e.g. build_type)
                        // before the in-popup question flow starts.
                        // Restore any persisted progress for the
                        // target category, then merge the prefill on
                        // top so the user resumes from the first
                        // unanswered question.
                        const saved = _loadProgress(opt.category);
                        const mergedRefinement = {
                          ...(saved?.refinement || {}),
                          ...(opt.prefill || {}),
                        };
                        setRefinement(mergedRefinement);
                        setCurrentQIdx(typeof saved?.qIdx === 'number' ? saved.qIdx : 0);
                        setPickedCategory(opt.category);
                        setStep('questions');
                      }}
                      data-testid={`mm-pick-${(opt.category || opt.label).replace(/[:_\s]/g, '-').toLowerCase()}`}
                      className="bp-mm-pick-pill"
                    >
                      <img
                        src={opt.iconSrc}
                        alt=""
                        aria-hidden="true"
                        className="bp-mm-pick-pill-icon"
                        loading="lazy"
                      />
                      <span className="bp-mm-pick-pill-label">{opt.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 6,
                }}
              >
                {categoryOptions.map((opt) => {
                  // 2026-06-09: Render the same Lucide icon the
                  // corresponding mega-menu circle uses, so the matcher
                  // visually mirrors the menu the user just came from.
                  // 2026-02-18: When the option supplies a 3D `iconSrc`
                  // PNG, prefer that over the Lucide outline so the
                  // matcher inherits the homepage's illustration style.
                  const LucideIcon = opt.LucideIcon;
                  const iconSrc = opt.iconSrc;
                  return (
                <button
                  key={opt.category}
                  type="button"
                  onClick={() => {
                    setPickedCategory(opt.category);
                    setStep('questions');
                  }}
                  data-testid={`mm-pick-${opt.category.replace(/[:_]/g, '-')}`}
                  className="bp-mm-pick-card"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.30)',
                    borderRadius: 999,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s ease, border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    minHeight: 52,
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.22)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.60)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(118,75,162,0.32)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.30)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {iconSrc ? (
                    <span
                      aria-hidden="true"
                      style={{
                        flexShrink: 0,
                        width: 36,
                        height: 36,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={iconSrc}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 4px 10px rgba(91,58,163,0.18))',
                        }}
                      />
                    </span>
                  ) : LucideIcon && (
                    <span
                      aria-hidden="true"
                      style={{
                        flexShrink: 0,
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.30)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                      }}
                    >
                      <LucideIcon size={17} strokeWidth={1.9} />
                    </span>
                  )}
                  {/* 2026-02-22: subtitle removed - too much vertical
                      scroll on phones with 10+ categories. Label-only
                      keeps the list scannable in one glance. */}
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', lineHeight: 1.25, flex: 1, minWidth: 0 }}>{opt.label}</span>
                </button>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* ─── Details step ────────────────────────────────── */}
          {step === 'account' && (
            <div className="bp-mm-form">
              <p className="bp-mm-hint">
                Last step before we shortlist your matches. We text a 6-digit code to verify your mobile so the business gets a real lead.
              </p>
              {/* Trust reassurance right before the user hands over a
                  phone number. Live App Store rating (dark variant for
                  the purple panel); hides itself if unavailable. */}
              <div className="mb-1" data-testid="mm-account-appstore-rating">
                <AppStoreRating variant="compact" dark />
              </div>
              <label className="bp-mm-field">
                <span>Full name</span>
                <input
                  type="text"
                  value={account.name}
                  onChange={(e) => setAccount({ ...account, name: e.target.value })}
                  autoComplete="name"
                  data-testid="mm-acc-name"
                />
              </label>
              <label className="bp-mm-field">
                <span>Email</span>
                <input
                  type="email"
                  value={account.email}
                  onChange={(e) => setAccount({ ...account, email: e.target.value })}
                  autoComplete="email"
                  data-testid="mm-acc-email"
                  disabled={!!user?.email}
                />
              </label>
              {/* Email-exists hint: surfaces the inline sign-in lane
                  so returning users don't have to leave the popup. */}
              {!user && emailExistsHint && !signinOpen && (
                <button
                  type="button"
                  onClick={() => { setSigninOpen(true); setError(''); }}
                  data-testid="mm-signin-prompt"
                  className="bp-mm-signin-prompt"
                >
                  You have a BuildPilot account · Sign in instead →
                </button>
              )}
              {/* Inline sign-in lane (email already entered above). */}
              {!user && signinOpen && (
                <div className="bp-mm-signin-lane" data-testid="mm-signin-lane">
                  <label className="bp-mm-field">
                    <span>Password</span>
                    <input
                      type="password"
                      value={signinPassword}
                      onChange={(e) => { setSigninPassword(e.target.value); if (signinError) setSigninError(''); }}
                      autoComplete="current-password"
                      placeholder="Your BuildPilot password"
                      data-testid="mm-signin-password"
                    />
                  </label>
                  {signinError && <p className="bp-mm-error" data-testid="mm-signin-error">{signinError}</p>}
                  <div className="bp-mm-signin-actions">
                    <button
                      type="button"
                      onClick={submitSignin}
                      disabled={signinSubmitting}
                      data-testid="mm-signin-submit"
                      className="bp-mm-signin-submit"
                    >
                      {signinSubmitting ? 'Signing in…' : 'Sign in &amp; continue →'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSigninOpen(false); setSigninPassword(''); setSigninError(''); }}
                      data-testid="mm-signin-cancel"
                      className="bp-mm-signin-cancel"
                    >
                      Continue as new
                    </button>
                  </div>
                  <a
                    href="/forgot-password"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bp-mm-signin-forgot"
                  >
                    Forgot password?
                  </a>
                </div>
              )}
              <div className="bp-mm-field-row">
                <label className="bp-mm-field bp-mm-field-cc">
                  <span>Country</span>
                  <select
                    value={account.countryCode}
                    onChange={(e) => setAccount({ ...account, countryCode: e.target.value })}
                    data-testid="mm-acc-cc"
                  >
                    <option value="+61">+61 AU</option>
                    <option value="+64">+64 NZ</option>
                    <option value="+1">+1 US</option>
                    <option value="+44">+44 UK</option>
                  </select>
                </label>
                <label className="bp-mm-field bp-mm-field-phone">
                  <span>Mobile number</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={account.phone}
                    placeholder="412 345 678"
                    onChange={(e) => setAccount({ ...account, phone: e.target.value })}
                    autoComplete="tel"
                    data-testid="mm-acc-phone"
                  />
                </label>
              </div>
              <div className="bp-mm-field-row" style={{ gridTemplateColumns: '110px 1fr' }}>
                <label className="bp-mm-field">
                  <span>State</span>
                  <select
                    value={account.state}
                    onChange={(e) => setAccount({ ...account, state: e.target.value })}
                    data-testid="mm-acc-state"
                  >
                    <option value="">Select…</option>
                    {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="bp-mm-field">
                  <span>Suburb of the work</span>
                  <SuburbCombo
                    value={account.suburb}
                    state={account.state}
                    onChange={(v) => setAccount({ ...account, suburb: v })}
                  />
                </label>
              </div>

              <label className="bp-mm-check">
                <input
                  type="checkbox"
                  checked={account.privacy}
                  onChange={(e) => setAccount({ ...account, privacy: e.target.checked })}
                  data-testid="mm-acc-privacy"
                />
                <span>
                  I agree to BuildPilot's <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </span>
              </label>
              <label className="bp-mm-check">
                <input
                  type="checkbox"
                  checked={account.leadShare}
                  onChange={(e) => setAccount({ ...account, leadShare: e.target.checked })}
                  data-testid="mm-acc-leadshare"
                />
                <span>
                  I'd like BuildPilot to introduce me to the businesses I select. BuildPilot may receive a Success Fee if I go on to engage one of them.
                </span>
              </label>

              {error && <p className="bp-mm-error">{error}</p>}

              <button
                type="button"
                className="bp-mm-primary"
                onClick={advanceFromAccount}
                disabled={otpSending}
                data-testid="mm-acc-continue"
              >
                {otpSending ? 'Sending code…' : 'Continue'} <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* ─── Verify mobile step ──────────────────────────── */}
          {step === 'verify' && (
            <div className="bp-mm-form" data-testid="mm-verify-step">
              <p className="bp-mm-hint">
                We sent a 6-digit code to <strong>{fullPhone}</strong>. Enter it below to verify your mobile.
              </p>
              {otpDevCode && (
                <p className="bp-mm-hint" style={{ background: 'rgba(29,142,254,0.08)', padding: '8px 12px', borderRadius: 8 }}>
                  <strong>Dev mode:</strong> code is <code data-testid="mm-otp-dev-code">{otpDevCode}</code>
                </p>
              )}
              <label className="bp-mm-field">
                <span>6-digit code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                  autoComplete="one-time-code"
                  data-testid="mm-otp-code"
                  style={{ letterSpacing: '0.4em', textAlign: 'center', fontSize: 18, fontWeight: 600 }}
                />
              </label>

              {error && <p className="bp-mm-error">{error}</p>}

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="bp-mm-secondary"
                  onClick={() => { setStep('account'); setOtpCode(''); setError(''); }}
                  data-testid="mm-otp-back"
                >
                  Back
                </button>
                <button
                  type="button"
                  className="bp-mm-primary"
                  onClick={verifyPhoneOtp}
                  disabled={otpVerifying || otpCode.length !== 6}
                  data-testid="mm-otp-verify"
                  style={{ flex: 1 }}
                >
                  {otpVerifying ? 'Verifying…' : 'Verify mobile'} <ChevronRight size={16} />
                </button>
              </div>
              <button
                type="button"
                className="bp-mm-text-btn"
                onClick={resendOtp}
                disabled={otpResendIn > 0 || otpSending}
                data-testid="mm-otp-resend"
              >
                {otpResendIn > 0 ? `Resend code in ${otpResendIn}s` : 'Resend code'}
              </button>
            </div>
          )}

          {/* ─── Questions step ─────────────────────────────────
              2026-feb: rebuilt as a ONE-AT-A-TIME flow. The question
              text replaces the static category-picker subheading and
              options render as large frosted-glass pills (visually
              identical to the pick-category icon pills the user
              already approved). Single-select questions auto-advance
              on tap; multiselect questions show a Continue button.
              Progress (current question + answers) persists to
              localStorage so closing + reopening the popup resumes
              the user exactly where they left off. */}
          {step === 'shop' && (
            <div className="bp-mm-form" data-testid="mm-shop-step">
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <BuildPilotIntelligenceBadge variant="dark" testId="mm-shop-intel-badge" />
              </div>
              <p className="bp-mm-hint" style={{ textAlign: 'center', marginBottom: 14 }}>
                {shopOnly
                  ? (shopHasRetail
                      ? 'Shop these products online - tap to go straight to the store. No forms, no wait.'
                      : 'Get your free quotes direct - tap to go straight to their form. No waiting.')
                  : 'Go direct below, or continue to get quotes from local suppliers.'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {shopResults.map((b, idx) => {
                  const isQuoteCard = b.result_kind === 'quote';
                  const shopHref = b.shop_url
                    ? (/^https?:\/\//i.test(b.shop_url) ? b.shop_url : `${process.env.REACT_APP_BACKEND_URL}${b.shop_url}`)
                    : '#';
                  const sLogo = resolveBuilderLogo(b);
                  return (
                    <div
                      key={b.id || idx}
                      data-testid={`mm-shop-card-${b.id || idx}`}
                      style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, background: '#fff', borderRadius: 16, padding: '12px 14px' }}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: 10, flex: '0 0 auto', background: b.logo_plate_style === 'white' ? '#fff' : (b.brand_color || '#1d8efe'), display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1px solid #eee' }}>
                        {sLogo
                          ? <img src={sLogo} alt={b.business_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                          : <span style={{ fontWeight: 700, color: '#1d8efe' }}>{(b.business_name || '?').slice(0, 1)}</span>}
                      </div>
                      <div style={{ flex: '1 1 130px', minWidth: 130, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 700, color: '#17123b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.business_name}</div>
                        {b.tagline && (
                          <div style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.tagline}</div>
                        )}
                        {!isQuoteCard && (
                          <span data-testid={`mm-shop-affiliate-${b.id || idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3, background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 700, lineHeight: 1.5 }}>Retail / Affiliate</span>
                        )}
                      </div>
                      <a
                        href={shopHref}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => trackImpression({ category, sourcePlacement, businessId: b.id, partnerKey: b.partner_key, userId: user?.id, sessionId: getOrCreateSessionId(), shownPosition: idx + 1, clicked: true })}
                        data-testid={`mm-shop-${b.id || idx}`}
                        style={{ flex: '1 1 auto', justifyContent: 'center', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#17123b', color: '#fff', borderRadius: 999, padding: '9px 16px', fontWeight: 600, textDecoration: 'none', fontSize: 14 }}
                      >
                        {isQuoteCard ? (b.cta_label || 'Get free quotes') : 'Shop now'} <ArrowUpRight size={14} />
                      </a>
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', textAlign: 'center', marginTop: 12 }}>
                {shopHasRetail
                  ? 'These are retail partners. BuildPilot may earn a commission from purchases. Suggestions only, not endorsements.'
                  : 'Suggestions only, not endorsements. BuildPilot may earn a referral fee.'}
              </p>
              {!shopOnly && (
                <button
                  type="button"
                  className="bp-mm-primary"
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={() => setStep('questions')}
                  data-testid="mm-shop-continue"
                >
                  Continue - get quotes from suppliers <ChevronRight size={14} />
                </button>
              )}
            </div>
          )}

          {step === 'questions' && (
            <div className="bp-mm-form bp-mm-questions-one-at-a-time">
              {loading && <p className="bp-mm-hint">Loading your questions…</p>}
              {!loading && refinementQs.length === 0 && (
                <p className="bp-mm-hint">We've got everything we need. See your matches below.</p>
              )}
              {!loading && refinementQs.length > 0 && (() => {
                // Show the question at currentQIdx. We rely on the
                // open-effect + pick-category onClick to set
                // currentQIdx to the first UNANSWERED question (so
                // prefilled / persisted answers don't ask the same
                // question twice). Render is otherwise straightforward.
                let qIdx = Math.max(0, Math.min(currentQIdx, refinementQs.length - 1));
                // If the current question is hidden by show_if (e.g.
                // user changed an upstream answer), auto-advance to
                // the next visible one rather than render an empty
                // question.
                if (refinementQs[qIdx] && !_shouldShowQ(refinementQs[qIdx], refinement)) {
                  let n = qIdx + 1;
                  while (n < refinementQs.length && !_shouldShowQ(refinementQs[n], refinement)) n += 1;
                  if (n < refinementQs.length) {
                    setTimeout(() => setCurrentQIdx(n), 0);
                    return <p className="bp-mm-hint">Skipping…</p>;
                  }
                  // No more visible questions → finish.
                  setTimeout(() => advanceToResults(), 0);
                  return <p className="bp-mm-hint">Locking in your match brief…</p>;
                }
                const rawQ = refinementQs[qIdx];
                // 2026-feb: Filter region options by the state the
                // user picked in the previous question, so a VIC user
                // doesn't see Adelaide regions. Falls back to the
                // full list when no state has been selected yet.
                const q = (() => {
                  if (!rawQ) return rawQ;
                  if (rawQ.id !== 'region') return rawQ;
                  const selectedState = (refinement.state || '').toString().toUpperCase();
                  if (!selectedState) return rawQ;
                  const filtered = (rawQ.options || []).filter(
                    (o) => !o.state || o.state.toUpperCase() === selectedState
                  );
                  return filtered.length ? { ...rawQ, options: filtered } : rawQ;
                })();
                if (!q) {
                  // All questions answered - auto-advance to the next
                  // matcher step. For the builder flow we hand off to
                  // the full-screen Find a Builder OTP/results page;
                  // every other category continues with the in-popup
                  // contact form.
                  setTimeout(() => advanceToResults(), 0);
                  return <p className="bp-mm-hint">Locking in your match brief…</p>;
                }
                const isBudget = BUDGET_QUESTION_IDS.has(q.id);
                const bench = isBudget ? BUDGET_BENCHMARKS[q.id] : null;
                const showNudge = bench && bench.flagValues.includes(refinement[q.id]);
                const isMulti = q.type === 'multiselect';
                const isText = q.type === 'text';
                const selectedMulti = isMulti && Array.isArray(refinement[q.id]) ? refinement[q.id] : [];
                const toggleMulti = (val) => {
                  const cur = selectedMulti.includes(val)
                    ? selectedMulti.filter((v) => v !== val)
                    : (q.max && selectedMulti.length >= q.max ? selectedMulti : [...selectedMulti, val]);
                  setRefinement({ ...refinement, [q.id]: cur });
                };
                // Total / position used by the in-question progress
                // counter. Count only VISIBLE questions so the
                // denominator reflects the user's actual path.
                const visibleIdxs = refinementQs
                  .map((qq, i) => (_shouldShowQ(qq, refinement) ? i : -1))
                  .filter((i) => i !== -1);
                const total = visibleIdxs.length;
                const positionInVisible = visibleIdxs.indexOf(qIdx) + 1;
                const positionLabel = `${positionInVisible || qIdx + 1} of ${total || refinementQs.length}`;
                // Back navigation steps to the previous VISIBLE question
                // (skipping over any hidden ones).
                const goPrev = () => {
                  let p = qIdx - 1;
                  while (p >= 0 && !_shouldShowQ(refinementQs[p], refinement)) p -= 1;
                  if (p >= 0) setCurrentQIdx(p);
                };
                // Compute the next unanswered & VISIBLE question index
                // starting from `fromIdx + 1`, against the freshly-
                // merged refinement state. Hidden questions are
                // skipped entirely. Returns refinementQs.length when
                // there are none left - caller treats that as "finished".
                const nextUnanswered = (fromIdx, mergedRef) => {
                  let n = fromIdx + 1;
                  while (n < refinementQs.length) {
                    const qq = refinementQs[n];
                    if (!_shouldShowQ(qq, mergedRef)) {
                      n += 1;
                      continue;
                    }
                    const v = mergedRef[qq.id];
                    const ans = qq.type === 'multiselect'
                      ? Array.isArray(v) && v.length > 0
                      : v !== undefined && v !== null && v !== '';
                    if (!ans) break;
                    n += 1;
                  }
                  return n;
                };
                // ─── H&L handoff ────────────────────────────────
                // When the user picks the option flagged with
                // `handoff === "hnl"` (the "Yes, show me packages"
                // answer on review_hl_packages), close MiniMatcher
                // and pop the H&L matcher in its place. Visually
                // seamless because both popups share the same
                // bp-mm-overlay/panel skeleton.
                const handoffToHnl = (mergedRefinement) => {
                  try {
                    // Persist the builder brief so the H&L matcher
                    // (and downstream tools) can pick it up.
                    window.sessionStorage.setItem(
                      'bp.hnl.from-builder.handoff',
                      JSON.stringify({ answers: mergedRefinement, ts: Date.now() }),
                    );
                  } catch { /* ignore */ }
                  _clearProgress(category);
                  onClose?.();
                  window.dispatchEvent(new CustomEvent('buildpilot:open-hnl-matcher', {
                    detail: {
                      sourcePlacement: 'find-a-builder-handoff',
                      seed: {
                        state: mergedRefinement.state || null,
                        budget: mergedRefinement.budget || null,
                      },
                    },
                  }));
                };
                // Centralised "finish or advance" handler that uses
                // the FRESHLY-merged refinement map (not stale state)
                // to decide whether to hand off or step to the next
                // question. Critical: advanceToResults() reads from
                // useCallback closure → would otherwise see the old
                // refinement on the very last click.
                const finishOrNext = (mergedRefinement) => {
                  // 2026-02-19: When the user answers the state question,
                  // mirror the value into account.state so the recommend
                  // refetch + impression tracking fire with the correct
                  // state (the deps array on the recommend useEffect
                  // watches account.state, not refinement.state).
                  if (q.id === 'state' && mergedRefinement.state && mergedRefinement.state !== account.state) {
                    setAccount({ ...account, state: mergedRefinement.state });
                  }
                  const next = nextUnanswered(qIdx, mergedRefinement);
                  if (next < refinementQs.length) {
                    setCurrentQIdx(next);
                    return;
                  }
                  // No more questions. For the builder flow we hand
                  // off to the full-screen Find a Builder tool with
                  // the freshly-merged answers; otherwise we continue
                  // with the in-popup contact form.
                  // 2026-02-17: Same handoff suppression as
                  // advanceToResults - when the backend reports zero
                  // coverage we keep the user inside the popup so they
                  // see the unified "still onboarding builders"
                  // concierge view instead of being sent to the full
                  // screen tool to see the same empty state.
                  const handoffOk = category === 'professional:builder'
                    && recs.coverage_state !== 'concierge'
                    && recs.coverage_state !== 'disabled';
                  if (handoffOk && typeof window !== 'undefined') {
                    try {
                      window.sessionStorage.setItem(
                        'bp.findbuilder.mm.handoff',
                        JSON.stringify({ answers: mergedRefinement, ts: Date.now() }),
                      );
                    } catch { /* ignore */ }
                    _clearProgress(category);
                    onClose?.();
                    window.location.assign('/workspace/tools/find-a-builder?skip-matcher=1&mm-handoff=1');
                    return;
                  }
                  // Fallback for non-builder flows - use the standard
                  // path which decides between 'account' and 'results'.
                  // skipCompleteness=true: we already know all visible
                  // questions are answered (we just walked them).
                  advanceToResults({ skipCompleteness: true });
                };
                return (
                  <div key={q.id} className="bp-mm-question bp-mm-question-single" data-testid={`mm-q-active-${q.id}`}>
                    <div className="bp-mm-question-head">
                      {qIdx > 0 && (
                        <button
                          type="button"
                          className="bp-mm-question-back"
                          onClick={goPrev}
                          data-testid="mm-q-back"
                          aria-label="Previous question"
                        >
                          ← Back
                        </button>
                      )}
                      <span className="bp-mm-question-counter" aria-hidden="true">{positionLabel}</span>
                    </div>
                    {/* Question text intentionally not rendered here -
                        it lives in the popup header now (see
                        `headerTitle` above) so the popup reads as a
                        single interview question per screen. */}
                    {isText ? (
                      <div className="bp-mm-question-text-wrap">
                        {/* Suburb-flavoured text questions get the
                            typeahead combo. We detect this via the
                            question id (any *_suburb / suburb) OR by
                            the placeholder mentioning the word. Falls
                            back to a plain input for free-text Qs. */}
                        {(/suburb/i.test(q.id) || /suburb/i.test(q.placeholder || '')) ? (
                          <SuburbCombo
                            value={refinement[q.id] || ''}
                            state={refinement.state || account.state || null}
                            onChange={(v) => setRefinement({ ...refinement, [q.id]: v })}
                          />
                        ) : (
                          <input
                            type="text"
                            className="bp-mm-question-text-input"
                            placeholder={q.placeholder || 'Type your answer…'}
                            value={refinement[q.id] || ''}
                            onChange={(e) => setRefinement({ ...refinement, [q.id]: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (refinement[q.id] || '').trim().length >= 2) {
                                e.preventDefault();
                                finishOrNext({ ...refinement, [q.id]: (refinement[q.id] || '').trim() });
                              }
                            }}
                            autoFocus
                            data-testid={`mm-q-text-${q.id}`}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="bp-mm-pick-pills bp-mm-question-pills">
                        {(q.options || []).map((opt) => {
                          const active = isMulti
                            ? selectedMulti.includes(opt.value)
                            : refinement[q.id] === opt.value;
                          return (
                            <button
                              type="button"
                              key={opt.value}
                              className={`bp-mm-pick-pill bp-mm-question-pill ${active ? 'is-active' : ''}`}
                              onClick={() => {
                                // Clear any stale "Please answer…" warning the
                                // moment the user actually picks something.
                                if (error) setError('');
                                if (isMulti) {
                                  toggleMulti(opt.value);
                                  return;
                                }
                                const merged = { ...refinement, [q.id]: opt.value };
                                setRefinement(merged);
                                // Gate short-circuit: private certifier + any
                                // future gated category. If the selected option
                                // is marked gate_fail, skip results and render
                                // the "not-ready" screen instead of continuing.
                                if (opt.gate_fail) {
                                  setTimeout(() => {
                                    setGate({
                                      reason: opt.gate_reason || 'unknown',
                                      category,
                                      state: merged.state || account.state || null,
                                    });
                                    setStep('gate');
                                  }, 220);
                                  return;
                                }
                                // H&L handoff short-circuit: when the
                                // selected option carries a handoff
                                // marker, jump to the H&L matcher
                                // instead of continuing the builder
                                // questionnaire.
                                if (opt.handoff === 'hnl') {
                                  setTimeout(() => handoffToHnl(merged), 220);
                                  return;
                                }
                                // Auto-advance to the next question
                                // after a brief visual confirmation.
                                setTimeout(() => finishOrNext(merged), 220);
                              }}
                              data-testid={`mm-q-${q.id}-${opt.value}`}
                            >
                              <span className="bp-mm-pick-pill-label">{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {isMulti && q.max && (
                      <p className="bp-mm-q-hint" style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', margin: '6px 0 0' }}>
                        {selectedMulti.length}/{q.max} selected
                      </p>
                    )}
                    {showNudge && (
                      <div className="bp-mm-budget-nudge" data-testid={`mm-budget-nudge-${q.id}`}>
                        <strong>Heads up.</strong> {bench.msg}
                      </div>
                    )}
                    {error && <p className="bp-mm-error">{error}</p>}
                    {isMulti && (
                      <button
                        type="button"
                        className="bp-mm-primary"
                        onClick={() => finishOrNext(refinement)}
                        disabled={selectedMulti.length === 0 || loading}
                        data-testid="mm-q-multi-continue"
                      >
                        Continue <ChevronRight size={16} />
                      </button>
                    )}
                    {isText && (
                      <button
                        type="button"
                        className="bp-mm-primary"
                        onClick={() => finishOrNext({ ...refinement, [q.id]: (refinement[q.id] || '').trim() })}
                        disabled={(refinement[q.id] || '').trim().length < 2 || loading}
                        data-testid="mm-q-text-continue"
                      >
                        Continue <ChevronRight size={16} />
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ─── Gate step ────────────────────────────────────────
              Reached when a refinement question option carried the
              `gate_fail: true` marker. Renders a friendly "not
              ready yet" screen with a reason-specific explanation,
              educational links, and soft handoff to related
              matchers. First user: professional:private_certifier.

              Reason keys are set server-side and must match the
              `gate_reason` values on gated options in
              backend/routes/opportunities.py. */}
          {step === 'gate' && gate && (
            <GateNotReady
              gate={gate}
              onOpenMatcher={(nextCategory) => {
                _clearProgress(category);
                setGate(null);
                onClose?.();
                window.dispatchEvent(new CustomEvent('buildpilot:open-matcher', {
                  detail: {
                    category: nextCategory,
                    sourcePlacement: `gate-handoff-from-${(category || '').replace(':', '-')}`,
                  },
                }));
              }}
              onClose={() => {
                _clearProgress(category);
                setGate(null);
                onClose?.();
              }}
            />
          )}

          {/* ─── Results step ─────────────────────────────────── */}
          {step === 'results' && (
            <div className="bp-mm-results">
              {/* Trust reinforcement badge - shown above every results
                  view (shortlist, concierge fallback, or empty state).
                  Reinforces algorithmic/independent matching at the
                  post-triage moment. */}
              <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'center' }}>
                <BuildPilotIntelligenceBadge
                  variant="dark"
                  testId="mm-results-intel-badge"
                />
              </div>
              {recs.coverage_state === 'concierge' || recs.coverage_state === 'disabled' || noCoverage || leadResults.length === 0 ? (
                /* 2026-06-05: Stripped the "BuildPilot Concierge" badge,
                   the bordered hero panel, the icon-laden trust pills,
                   and the "within 1-2 business days" promise. The new
                   copy asks soft permission to follow up when more
                   businesses come online - leaves room for marketing
                   campaigns / drip emails without committing the team
                   to a personal SLA. Inline inside the existing matcher
                   container; no separate UX surface.
                   2026-02-17: Added an email-only capture so anonymous
                   users (who never reached the OTP gate because of the
                   zero-coverage short-circuit) can still get a heads-up.
                   2026-02-17 (b): Unified the legacy "fallback" branch
                   (coverage_state=available but zero results returned)
                   into this same concierge view so every empty-result
                   state looks identical across all matcher categories.
                */
                <div className="bp-mm-concierge-soft" data-testid="mm-concierge">
                  <p className="bp-mm-concierge-soft-msg">
                    {(() => {
                      // Derive a friendly category noun. For the builder
                      // category we hard-code "builders" because the
                      // heading reads "Find your perfect builder" and
                      // pluralising would produce "perfect builders".
                      let noun = null;
                      if (category === 'professional:builder') {
                        noun = 'builders';
                      } else {
                        const m = (heading || '').match(/^find your\s+(.+?)$/i);
                        if (m) {
                          noun = m[1].trim().replace(/[.?!]+$/, '');
                          // Strip stylistic prefixes that don't survive
                          // the pluralised "We're still onboarding X" phrasing
                          noun = noun.replace(/^(perfect|ideal|best)\s+/i, '');
                          if (!/(s|ies)$/i.test(noun)) noun = noun + 's';
                        }
                      }
                      if (noun) {
                        const where = (account.state || initialState) ? `in ${account.state || initialState}` : 'in your area';
                        return `We're still onboarding ${noun} ${where}. With your permission we'll reach out the moment we have a great fit - no obligation, just a heads-up.`;
                      }
                      return recs.fallback?.message
                        || "We're still onboarding businesses in this category in your area. With your permission we'll reach out the moment we have a great fit - no obligation, just a heads-up.";
                    })()}
                  </p>

                  {/* 2026-02-15: Tools + guides "keep moving" block.
                      Shown only when fallbackResolver returns content
                      for the matcher's category. Phase 2 (2026-02-15b)
                      passes the user's `refinement` answers so the
                      resolver can swap in answer-aware variants. */}
                  {(() => {
                    // 2026-02-15 (d): pass the captured suburb (account
                    // form OR any *_suburb refinement answer) so the
                    // resolver can prepend a /best-builder-{suburb} or
                    // /building-in/{suburb} guide when one exists.
                    const capturedSuburb =
                      account.suburb
                      || refinement.suburb
                      || refinement.land_suburb
                      || refinement.work_suburb
                      || null;
                    const fb = resolveFallback(category, refinement, { suburb: capturedSuburb });
                    if (!fb || ((fb.tools.length === 0) && (fb.guides.length === 0))) return null;
                    return (
                      <div className="bp-mm-fallback-suggest" data-testid="mm-fallback-suggest">
                        {fb.tools.length > 0 && (
                          <div className="bp-mm-fallback-group" data-testid="mm-fallback-tools">
                            <p className="bp-mm-fallback-eyebrow">Keep moving - use these now</p>
                            <div className="bp-mm-fallback-tiles">
                              {fb.tools.slice(0, 3).map((t) => (
                                <a
                                  key={t.url}
                                  href={t.url}
                                  className="bp-mm-fallback-tile bp-mm-fallback-tile--tool"
                                  data-testid={`mm-fallback-tool-${t.url.split('/').pop()}`}
                                >
                                  <span className="bp-mm-fallback-tile-eyebrow">{t.eyebrow}</span>
                                  <span className="bp-mm-fallback-tile-label">{t.label}</span>
                                  <ArrowUpRight size={14} className="bp-mm-fallback-tile-arrow" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {fb.guides.length > 0 && (
                          <div className="bp-mm-fallback-group" data-testid="mm-fallback-guides">
                            <p className="bp-mm-fallback-eyebrow">Read while you wait</p>
                            <ul className="bp-mm-fallback-guides">
                              {fb.guides.slice(0, 3).map((g) => (
                                <li key={g.url}>
                                  <a
                                    href={g.url}
                                    className="bp-mm-fallback-guide"
                                    data-testid={`mm-fallback-guide-${g.url.split('/').filter(Boolean).pop()}`}
                                  >
                                    {g.isSuburb ? (
                                      <MapPin size={13} className="bp-mm-fallback-guide-icon" />
                                    ) : (
                                      <BookOpen size={13} className="bp-mm-fallback-guide-icon" />
                                    )}
                                    <span className="bp-mm-fallback-guide-label">{g.label}</span>
                                    {g.readMin && (
                                      <span className="bp-mm-fallback-guide-read">{g.readMin} min</span>
                                    )}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* 2026-02-15: SA-only 1-on-1 advisor offer for the
                      genuinely-complex matcher categories where
                      self-serve is incomplete: heritage / whole-home
                      renovations, extensions without drawings,
                      sloping blocks, etc. The component itself
                      hides outside SA. */}
                  {(() => {
                    const complexCats = new Set([
                      'professional:renovation_builder',
                      'professional:extension_builder',
                      'professional:builder',
                    ]);
                    const complexAnswers = (
                      refinement?.reno_scope === 'heritage' ||
                      refinement?.reno_scope === 'whole_home' ||
                      refinement?.drawings_ready === 'no_drawings' ||
                      refinement?.extension_type === 'second_storey' ||
                      (refinement?.slope && refinement.slope !== 'flat' && refinement.slope !== 'unsure')
                    );
                    if (!complexCats.has(category) || !complexAnswers) return null;
                    return (
                      <AdvisorOffer
                        placement={`matcher-${category}`}
                        state={account.state || initialState}
                        user={user}
                        variant="compact"
                      />
                    );
                  })()}

                  {!user && !account.email && (
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="Email me when we have a match"
                      value={account.email}
                      onChange={(e) => setAccount({ ...account, email: e.target.value })}
                      className="bp-mm-concierge-soft-input"
                      data-testid="mm-concierge-email"
                    />
                  )}
                  {/* 2026-02-15: explicit consent. Auto-bypassed for logged-in
                      users who already ticked it at sign-up. */}
                  {!user && (
                    <label className="bp-mm-concierge-consent" data-testid="mm-concierge-consent-wrap">
                      <input
                        type="checkbox"
                        checked={conciergeConsent}
                        onChange={(e) => setConciergeConsent(e.target.checked)}
                        data-testid="mm-concierge-consent"
                      />
                      <span>
                        I agree to BuildPilot&apos;s{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>{' '}
                        and{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                      </span>
                    </label>
                  )}
                  {error && <p className="bp-mm-error">{error}</p>}
                  <button
                    type="button"
                    className="bp-mm-primary"
                    disabled={submitting}
                    onClick={submitConcierge}
                    data-testid="mm-concierge-submit"
                  >
                    {submitting ? 'Saving…' : 'Yes, keep me posted'}
                    <ChevronRight size={16} />
                  </button>
                </div>
              ) : (
                <>
                  {recs.coverage_state === 'partial' && recs.fallback?.message && (
                    <div className="bp-mm-partial-banner" data-testid="mm-partial-coverage-banner">
                      <div className="bp-mm-partial-banner-row">
                        <Compass size={14} className="bp-mm-partial-banner-icon" />
                        <p className="bp-mm-partial-banner-text">
                          {recs.fallback.message}
                        </p>
                      </div>
                      {waitlistStatus === 'success' ? (
                        <p className="bp-mm-partial-waitlist-success" data-testid="mm-waitlist-success">
                          ✓ You're on the list. We'll email you the moment new {recs.fallback?.category_label || 'businesses'} join.
                        </p>
                      ) : (
                        <>
                          <div className="bp-mm-partial-waitlist">
                            <input
                              type="email"
                              inputMode="email"
                              autoComplete="email"
                              value={waitlistEmail}
                              onChange={(e) => { setWaitlistEmail(e.target.value); if (waitlistError) setWaitlistError(''); }}
                              placeholder="Email me when more join"
                              disabled={waitlistStatus === 'submitting'}
                              className="bp-mm-partial-waitlist-input"
                              data-testid="mm-waitlist-email"
                            />
                            <button
                              type="button"
                              onClick={submitWaitlist}
                              disabled={waitlistStatus === 'submitting'}
                              className="bp-mm-partial-waitlist-btn"
                              data-testid="mm-waitlist-submit"
                            >
                              {waitlistStatus === 'submitting' ? 'Saving…' : 'Notify me'}
                            </button>
                          </div>
                          {!user && (
                            <label className="bp-mm-partial-waitlist-consent" data-testid="mm-waitlist-consent-wrap">
                              <input
                                type="checkbox"
                                checked={waitlistConsent}
                                onChange={(e) => setWaitlistConsent(e.target.checked)}
                                data-testid="mm-waitlist-consent"
                              />
                              <span>
                                I agree to BuildPilot&apos;s{' '}
                                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>{' '}
                                and{' '}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                              </span>
                            </label>
                          )}
                        </>
                      )}
                      {waitlistError && (
                        <p className="bp-mm-partial-waitlist-error" data-testid="mm-waitlist-error">{waitlistError}</p>
                      )}
                    </div>
                  )}
                  {/* 2026-02-23: Soft banner shown on 2nd+ completed run
                      for this category. Frames the shortlist refresh as
                      "live availability" rather than randomness - aligns
                      with the existing Recommendations Policy line about
                      outcomes depending on builder availability/capacity. */}
                  {isRerun && (
                    <div
                      className="bp-mm-rerun-banner"
                      data-testid="mm-rerun-banner"
                      style={{
                        margin: '0 0 14px 0',
                        padding: '12px 14px',
                        borderRadius: 14,
                        background: 'rgba(255,255,255,0.10)',
                        border: '1px solid rgba(255,255,255,0.22)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        color: '#ffffff',
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>
                        Your shortlist has refreshed
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: 12.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>
                        Businesses update what they&apos;re taking on all the time &mdash; current projects, suburbs they&apos;re serving, budget ranges and timing. We rematch your brief against who&apos;s the best fit <strong>right now</strong>, so the names you see may not be identical to last time. That&apos;s normal.
                      </p>
                    </div>
                  )}
                  {leadResults.map((b, idx) => {
                    const consentKey = b.partner_key || b.id;
                    const plateStyle = b.logo_plate_style === 'white' ? 'white' : 'brand';
                    const heroBg = plateStyle === 'white' ? '#ffffff' : (b.brand_color || '#1d8efe');
                    // Prefer the white-wordmark variant when the plate is filled
                    // with the brand colour, keeps legibility for partners
                    // (Equable) whose primary logo is dark navy on transparent.
                    const heroLogo = (plateStyle === 'brand' && b.logo_url_light)
                      ? b.logo_url_light
                      : resolveBuilderLogo(b);
                    const featureImg = b.feature_image_url;
                    const brandHex = b.brand_color || '#1d8efe';
                    // Slug for the per-business profile page. We derive
                    // from business_name when the row doesn't carry an
                    // explicit slug yet (no `slug` column on
                    // business_profiles). Same algorithm as the static
                    // suppliersData.js entries so existing routes still
                    // resolve correctly (e.g. "Best Solar and
                    // Batteries" → /suppliers/best-solar-and-batteries).
                    const businessSlug = b.slug || (b.business_name || '')
                      .toLowerCase()
                      .replace(/&/g, 'and')
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    // Category prefix → URL prefix. Per the unified
                    // profile-page plan (1b in the design choices):
                    // /suppliers/:slug, /tradies/:slug, /professionals/:slug,
                    // /builders/:slug. Suppliers route works today;
                    // the others get built in the next pass.
                    const categoryPrefix = (category || '').split(':')[0];
                    const profilePathPrefix = {
                      supplier: '/suppliers',
                      tradie: '/tradies',
                      professional: '/professionals',
                      partner: '/suppliers',     // partner overlays still live as supplier profiles
                    }[categoryPrefix] || '/business';
                    const profileHref = businessSlug ? `${profilePathPrefix}/${businessSlug}` : null;
                    const matchPct = Number.isFinite(b.match_score)
                      ? Math.max(0, Math.min(100, Math.round(b.match_score)))
                      : null;
                    // 2026-06: affiliate suppliers are "shop" cards - a tracked
                    // click to their store, NO contact capture. Distribution
                    // suppliers (Lamina Stone, Best Solar, Sustainable Concrete)
                    // stay as lead-capture cards exactly as before.
                    const isShop = b.result_kind === 'shop' || b.result_kind === 'quote';
                    const isQuote = b.result_kind === 'quote';
                    const shopHref = (isShop && b.shop_url)
                      ? (/^https?:\/\//i.test(b.shop_url) ? b.shop_url : `${process.env.REACT_APP_BACKEND_URL}${b.shop_url}`)
                      : null;
                    // Partnership badge (2026-06-17): show the business's
                    // relationship to BuildPilot at a glance. Priority
                    // builder > distribution > opportunity; advertising is
                    // never a matcher relationship so it's ignored here.
                    const PARTNERSHIP_BADGE = {
                      builder: 'Builder Partner',
                      distribution: 'Distribution Partner',
                      opportunity: 'Business Partner',
                    };
                    const partnerType = ['builder', 'distribution', 'opportunity']
                      .find((t) => (b.partnership_types || []).includes(t));
                    const partnerBadge = !b.is_partner && partnerType && !isShop
                      ? PARTNERSHIP_BADGE[partnerType]
                      : null;
                    // Build a query string for the verifier tools so the
                    // business name + state is prefilled when the user
                    // lands on the licence / insurance check tool.
                    const verifyQs = new URLSearchParams();
                    if (b.business_name) verifyQs.set('builder', b.business_name);
                    const verifyTail = verifyQs.toString();
                    const licenceHref   = `/workspace/tools/builder-licence-check${verifyTail ? `?${verifyTail}` : ''}`;
                    const insuranceHref = `/workspace/tools/building-insurance-check${verifyTail ? `?${verifyTail}` : ''}`;
                    return (
                      <article
                        key={consentKey}
                        className={`bp-mm-strip bp-mm-strip-branded bp-mm-card-plate-${plateStyle}`}
                        style={{ '--bp-mm-brand': brandHex }}
                        data-testid={`mm-card-${consentKey}`}
                      >
                        <div className="bp-mm-strip-head">
                          <div
                            className="bp-mm-strip-hero"
                            style={{
                              background: featureImg ? '#0a2540' : heroBg,
                            }}
                          >
                            {featureImg ? (
                              <img
                                src={featureImg}
                                alt={`${b.business_name} feature`}
                                className="bp-mm-strip-feature"
                                loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : heroLogo ? (
                              // No feature image: brand-colour plate
                              // shows the favicon-style logo on a small
                              // white circle so any-coloured brand mark
                              // (e.g. Best Solar's green leaf) always
                              // reads regardless of the plate colour.
                              <span className="bp-mm-strip-logo-plate">
                                <img
                                  src={heroLogo}
                                  alt={`${b.business_name} logo`}
                                  loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              </span>
                            ) : (
                              <span className="bp-mm-card-initials">
                                {(b.business_name || '?').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                              </span>
                            )}
                            {/* When the hero shows a feature image, surface
                                the business logo as a small badge in the
                                corner so brand identity stays visible. The
                                badge background stays white so a coloured
                                favicon-style mark always reads. */}
                            {featureImg && heroLogo && (
                              <span className="bp-mm-strip-logo-badge">
                                <img
                                  src={heroLogo}
                                  alt={`${b.business_name} logo`}
                                  loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              </span>
                            )}
                            {b.tier === 'featured' && !b.is_partner && (
                              <span className="bp-mm-strip-pill bp-mm-featured-pill" title="Featured: this business subscribes to BuildPilot.">Featured</span>
                            )}
                            {b.is_partner && (
                              <span className="bp-mm-strip-pill bp-mm-partner-pill" title="BuildPilot's exclusive partner for this category.">Partner</span>
                            )}
                            {isShop && !isQuote && (
                              <span className="bp-mm-strip-pill" style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' }} title="Retail / affiliate store - not a BuildPilot partner." data-testid={`mm-affiliate-pill-${consentKey}`}>Retail</span>
                            )}
                          </div>
                          <div className="bp-mm-strip-meta">
                            <div className="bp-mm-strip-name-row">
                              <h4 className="bp-mm-card-name">{b.business_name}</h4>
                              {matchPct !== null && matchPct > 0 && (
                                <span
                                  className="bp-mm-strip-match"
                                  data-testid={`mm-match-${consentKey}`}
                                  aria-label={`${matchPct}% match`}
                                >
                                  {matchPct}% match
                                </span>
                              )}
                            </div>
                            {partnerBadge && (
                              <span
                                className="bp-mm-partnership-badge"
                                data-testid={`mm-partnership-${consentKey}`}
                                title={`${partnerBadge} — this business partners with BuildPilot as a ${partnerBadge.toLowerCase()}.`}
                              >
                                <BadgeCheck size={11} /> {partnerBadge}
                              </span>
                            )}
                            {Array.isArray(b.fact_pills) && b.fact_pills.length > 0 && (
                              <div className="bp-mm-card-facts bp-mm-strip-facts">
                                {b.fact_pills.slice(0, 2).map((f, i) => {
                                  const Icon = ICONS[f.icon] || ShieldCheck;
                                  return (
                                    <span key={i} className="bp-mm-card-fact">
                                      <Icon size={10} /> {f.label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {Array.isArray(b.match_reasons) && b.match_reasons.length > 0 && (
                              <div className="bp-mm-why" data-testid={`mm-why-${consentKey}`}>
                                <p className="bp-mm-why-title">Why we matched you</p>
                                <ul className="bp-mm-why-list">
                                  {b.match_reasons.slice(0, 3).map((reason, i) => (
                                    <li key={i}><Check size={11} /> {reason}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {/* Verify-yourself row: lives in the meta column
                                so the card stays compact (3 cards visible
                                on a phone screen). */}
                            <div
                              className="bp-mm-strip-verify"
                              data-testid={`mm-verify-${consentKey}`}
                            >
                              {b.video_intro_url && (
                                <BusinessIntroVideo
                                  url={b.video_intro_url}
                                  businessName={b.business_name}
                                  variant="pill"
                                  size="xs"
                                />
                              )}
                              {profileHref && (
                                <a
                                  href={profileHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bp-mm-strip-verify-chip"
                                  data-testid={`mm-profile-${consentKey}`}
                                >
                                  <ArrowUpRight size={10} /> View profile
                                </a>
                              )}
                              <a
                                href={licenceHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bp-mm-strip-verify-chip"
                                data-testid={`mm-verify-licence-${consentKey}`}
                                style={{ display: isShop ? 'none' : undefined }}
                              >
                                <BadgeCheck size={10} /> Check licence
                              </a>
                              <a
                                href={insuranceHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bp-mm-strip-verify-chip"
                                data-testid={`mm-verify-insurance-${consentKey}`}
                                style={{ display: isShop ? 'none' : undefined }}
                              >
                                <ShieldCheck size={10} /> Check insurance
                              </a>
                            </div>
                            {Array.isArray(b.attachments) && b.attachments.length > 0 && (
                              /* 2026-02-28: partner capability statements (e.g. BSB
                                 commercial + residential PDFs). Rendered right below
                                 the verify chips so shortlisted users can download
                                 the partner's positioning material before consenting. */
                              <div
                                className="bp-mm-strip-attachments"
                                data-testid={`mm-attachments-${consentKey}`}
                                style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}
                              >
                                {b.attachments.slice(0, 3).map((a, aIdx) => (
                                  <a
                                    key={a.id || aIdx}
                                    href={a.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bp-mm-strip-verify-chip"
                                    title={a.summary || a.label}
                                    data-testid={`mm-attachment-${consentKey}-${aIdx}`}
                                  >
                                    <FileText size={10} /> {a.label}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {isShop ? (
                          /* 2026-06: affiliate supplier - tracked click to
                             their store, NO contact capture / consent. */
                          <a
                            href={shopHref || '#'}
                            target="_blank"
                            rel="noopener noreferrer sponsored"
                            className="bp-mm-primary bp-mm-strip-cta"
                            onClick={() => trackImpression({ category, sourcePlacement, businessId: b.id, partnerKey: b.partner_key, userId: user?.id, sessionId: getOrCreateSessionId(), shownPosition: idx + 1, clicked: true })}
                            data-testid={`mm-shop-${consentKey}`}
                          >
                            {isQuote ? (b.cta_label || 'Get free quotes') : `Shop at ${b.business_name}`}
                            <ArrowUpRight size={14} />
                          </a>
                        ) : (
                          <>
                            <label className="bp-mm-consent bp-mm-strip-consent">
                              <input
                                type="checkbox"
                                checked={!!perBusinessConsent[consentKey]}
                                onChange={(e) => setPerBusinessConsent({
                                  ...perBusinessConsent,
                                  [consentKey]: e.target.checked,
                                })}
                                data-testid={`mm-consent-${consentKey}`}
                              />
                              <span>
                                Yes, please introduce me. BuildPilot will share my contact details with <strong>{b.business_name}</strong>.
                              </span>
                            </label>
                            <button
                              type="button"
                              className="bp-mm-primary bp-mm-strip-cta"
                              disabled={!perBusinessConsent[consentKey] || submitting}
                              onClick={() => { trackImpression({ category, sourcePlacement, businessId: b.id, partnerKey: b.partner_key, userId: user?.id, sessionId: getOrCreateSessionId(), shownPosition: idx + 1, clicked: true }); connectMe(b); }}
                              data-testid={`mm-connect-${consentKey}`}
                            >
                              {submitting ? 'Sending introduction…' : 'Connect me'}
                              <ChevronRight size={14} />
                            </button>
                          </>
                        )}
                      </article>
                    );
                  })}
                  {/* 2026-02-23: Always-available "Why might my results
                      change?" expandable explainer. Visible to all users
                      (first-timers + returners) so the framing of "live
                      availability" is on tap if they're curious, without
                      cluttering the result cards. Matches the glass
                      aesthetic of the rerun banner. */}
                  {(recs.results || []).length > 0 && (
                    <details
                      className="bp-mm-why-change"
                      data-testid="mm-why-change"
                      open={whyChangeOpen}
                      onToggle={(e) => setWhyChangeOpen(e.currentTarget.open)}
                      style={{
                        marginTop: 14,
                        padding: '10px 14px',
                        borderRadius: 14,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.16)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        color: '#ffffff',
                      }}
                    >
                      <summary
                        data-testid="mm-why-change-toggle"
                        style={{
                          cursor: 'pointer',
                          listStyle: 'none',
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.92)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>
                          {whyChangeOpen ? '−' : '+'}
                        </span>
                        Why might my results change if I run this again?
                      </summary>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.85)' }}>
                        <li>Businesses update their availability, capacity and the project types they&apos;re taking on &mdash; sometimes weekly.</li>
                        <li>New businesses join the platform; others go on a quiet period or are paused.</li>
                        <li>Small changes to your brief (budget, suburb, timing) can shift who&apos;s the strongest fit.</li>
                        <li>Our matching engine refreshes against the most current information so you see who&apos;s a good fit <strong>right now</strong>, not 6 months ago.</li>
                      </ul>
                      <p style={{ margin: '8px 0 0 0', fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                        BuildPilot introduces you to businesses based on suitability. We don&apos;t guarantee a specific shortlist will be available at any future date.
                      </p>
                    </details>
                  )}
                  {error && <p className="bp-mm-error">{error}</p>}
                </>
              )}
            </div>
          )}

          {/* ─── Sent step ────────────────────────────────────── */}
          {step === 'sent' && (
            <div className="bp-mm-sent" data-testid="mm-sent-step">
              <div className="bp-mm-sent-icon">
                <Check size={24} strokeWidth={3} color="#1d8efe" />
              </div>
              {sentToBusinessName ? (
                <>
                  <h4>You&apos;re connected</h4>
                  <p data-testid="mm-sent-handoff-confirm">
                    You&apos;ve connected with <strong>{sentToBusinessName}</strong>.
                    We&apos;ve shared your name, contact details and project brief with them so they can be in touch as soon as they can.
                  </p>
                  <p
                    data-testid="mm-sent-handoff-rights"
                    style={{ fontSize: 12, color: '#64748b', marginTop: 8, lineHeight: 1.5 }}
                  >
                    Your contract for any work is with {sentToBusinessName}, not BuildPilot.
                    {' '}Always verify their licence (where required) and insurance before signing.
                  </p>
                </>
              ) : (
                <>
                  <h4>Thank you</h4>
                  <p>We&apos;ll come back to you once we have onboarded more businesses that can help you.</p>
                </>
              )}
              <button type="button" className="bp-mm-primary" onClick={onClose} data-testid="mm-sent-close">
                Close
              </button>
              {/* Live App Store rating on the confirmation screen -
                  a closing trust signal (dark variant for purple panel). */}
              <div className="mt-4 flex justify-center" data-testid="mm-sent-appstore-rating">
                <AppStoreRating variant="compact" dark />
              </div>
            </div>
          )}
        </div>

        <footer className="bp-mm-foot">
          <MatchDisclaimer inline className="!text-[10.5px] mb-1" />
          {(() => {
            // 1:1 human support is currently SA-only. Hide the link once a
            // non-SA state (e.g. VIC) is selected so we never promise help
            // we can't yet provide there.
            const stateNow = (refinement.state || account.state || '').toString().toUpperCase();
            if (stateNow && stateNow !== 'SA') return null;
            return (
              <a href="/one-on-one-support" className="bp-mm-foot-link">
                Or talk to BuildPilot 1:1 →
              </a>
            );
          })()}
        </footer>
      </aside>
      {/* Explainer video lightbox - rendered outside the aside so
          it can overlay the entire viewport when triggered. */}
      <MatcherExplainerVideoModal open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </div>
  );

  // Render via a portal directly to <body> so ancestor `transform`/
  // `filter`/`perspective` rules (e.g. the workspace mesh background
  // and any sticky containers) can't trap the fixed-position panel
  // inside their own containing block.
  if (typeof document === 'undefined') return overlay;
  return ReactDOM.createPortal(overlay, document.body);
}

/**
 * SuburbCombo - typeable input + filtered dropdown of Adelaide
 * suburbs. Falls back to a free-text input for non-SA states
 * (we only have a curated SA list today).
 *
 * Design choices:
 *  • Always-on dropdown when focused, even with empty input, so users
 *    who don't know the spelling can browse. Caps the visible list
 *    at 8 rows to keep the matcher viewport-friendly.
 *  • Case-insensitive contains-match, not just prefix-match (so
 *    "park" surfaces "Angle Park", "Salisbury Park", etc).
 *  • Selecting an option closes the dropdown and submits the value
 *    up to the parent. Free-typing still works for suburbs not
 *    on the curated list.
 */
function SuburbCombo({ value, state, onChange }) {
  const [focused, setFocused] = useState(false);
  const [remoteMatches, setRemoteMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const q = (value || '').trim();

  // 2026-02-17: AU-wide suburb typeahead. Replaces the SA-only bundled
  // list with a debounced fetch to /api/suburbs/search which serves
  // 17.5k AU suburbs (pgeocode AU postcode set). Each keystroke after
  // a 150 ms debounce hits the endpoint; an in-component AbortController
  // cancels stale requests so a fast typist doesn't see flickering
  // out-of-order responses.
  useEffect(() => {
    if (!focused) return undefined;
    if (q.length === 0) {
      // No query - clear remote, fall back to local SA suggestions
      setRemoteMatches([]);
      return undefined;
    }
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (!apiUrl) return undefined;
    const ctrl = new AbortController();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';
        const res = await fetch(`${apiUrl}/api/suburbs/search?q=${encodeURIComponent(q)}${stateParam}&limit=8`, {
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error('search failed');
        const data = await res.json();
        if (!ctrl.signal.aborted) setRemoteMatches(data.results || []);
      } catch (e) {
        if (e.name !== 'AbortError') setRemoteMatches([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 150);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, state, focused]);

  // Local SA suggestions only used when query is empty (gives a useful
  // starting hint for the dominant SA cohort).
  const fallbackList = useMemo(() => {
    if (q.length > 0) return [];
    return _ALL_SUBURBS_SA.slice(0, 8).map((name) => ({ name, state: 'SA', postcode: '' }));
  }, [q]);

  const list = remoteMatches.length > 0 ? remoteMatches : fallbackList;
  const showList = focused && list.length > 0;

  return (
    <div className="bp-mm-suburb-combo">
      <input
        type="text"
        className="bp-mm-question-text-input bp-mm-suburb-input"
        value={value}
        placeholder="Start typing your suburb…"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        autoComplete="off"
        data-testid="mm-acc-suburb"
      />
      {showList && (
        <ul className="bp-mm-suburb-menu" data-testid="mm-acc-suburb-menu" role="listbox">
          {list.map((s) => {
            const label = s.state ? `${s.name}, ${s.state}${s.postcode ? ` ${s.postcode}` : ''}` : s.name;
            return (
              <li
                key={`${s.name}-${s.state}-${s.postcode}`}
                role="option"
                data-testid={`mm-acc-suburb-opt-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                onMouseDown={(e) => { e.preventDefault(); onChange(s.name); setFocused(false); }}
              >
                {label}
              </li>
            );
          })}
          {loading && q && list.length === 0 && (
            <li className="bp-mm-suburb-loading" aria-disabled="true">Searching…</li>
          )}
        </ul>
      )}
    </div>
  );
}
